import React, { useState, useEffect, useCallback, useRef } from 'react';
import { neonQuery, fetchSalesReport } from '../services/api';
import { getCurrentUser } from '../services/auth';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

export const LOCATIONS = {
  F01: 'Blast Freezer',
  F02: 'Inside Freezer',
  SA1: 'Restaurant Cooler',
  C01: 'HCD Cooler',
  C02: 'Production Floor Cooler',
  R01: 'Production Floor Rack',
  R02: 'Seasoning Rack',
  R03: 'Completed Boxes',
  PB1: 'Front Pole Barn',
  PB2: 'Back Pole Barn',
  P01: 'Processing Room Hold',
  P02: 'Processing Room',
  PF1: 'Production Floor Sales Hold',
};

const REASON_CODES = ['sale', 'waste', 'processing', 'count_correction', 'transfer', 'other'];

const FAMILIES = [
  'Soft Crab', 'Hard Crab', 'Crab Cakes', 'Crab Meat', 'Shrimp',
  'Snow Crab', 'King Crab', 'Lobster', 'Scallops', 'Shellfish',
  'Fish & Seafood', 'Seasoning & Sauces', 'Packaging & Supplies', 'Accessories',
];

const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
const dateStr = () => new Date().toISOString();
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const fmtMoney = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const locLabel = (code) => `${code} — ${LOCATIONS[code] || code}`;
// Entry rules from the Receiving Workflow: whole numbers only, and SKUs and
// locations aren't case sensitive (always stored and shown in upper case).
const toWhole = (v) => String(v ?? '').replace(/[^0-9]/g, '');
const normLoc = (v) => String(v || '').trim().toUpperCase().replace(/\s+/g, '');
const isAdminUser = () => { try { return (getCurrentUser()?.roles || []).includes('Admin'); } catch { return false; } };

// ─────────────────────────────────────────────────────────────────────────────
// DATA LAYER
// ─────────────────────────────────────────────────────────────────────────────

async function imsQuery(queryName, params = {}) {
  try {
    const result = await neonQuery(queryName, params);
    return result?.rows ?? result ?? [];
  } catch (e) {
    console.error(`[IMS] ${queryName} failed:`, e.message);
    throw e;
  }
}

const loadItems        = () => imsQuery('ims_items_list_v2');
const loadPar          = async () => { const r = await neonQuery('ims_par_list'); return Object.fromEntries(((r && r.par) || []).map(x => [x.item_id, x])); };
const savePar          = (p) => neonQuery('ims_par_save', p);
const loadInventory    = () => imsQuery('ims_inventory_list');
const loadAuditLog     = (f) => imsQuery('ims_audit_log_list', f);
const loadOwnerSettings= () => imsQuery('ims_owner_settings_get').then(r => r[0] ?? { gross_profit: 85000, prev_total_value: 0, month: '' });
const loadSnapshots    = () => imsQuery('ims_snapshots_list');

const saveInventoryRecord = (r) => imsQuery('ims_inventory_upsert', r);
const deleteInventoryRecord = (loc, item_id) => imsQuery('ims_inventory_delete', { loc, item_id });
const appendAuditEntry = (e) => imsQuery('ims_audit_log_append', e);
const saveOwnerSettings= (s) => imsQuery('ims_owner_settings_upsert', s);
const saveSnapshot     = (s) => imsQuery('ims_snapshots_upsert', s);
// Phase 2 (CPX Catch): items + receiving
const loadVendors      = () => imsQuery('ims_vendors_list');
const loadOpenHolds    = () => imsQuery('ims_damage_holds_list', { status: 'open' });
const loadCostLog      = (item_id) => imsQuery('ims_cost_log_list', { item_id });
const saveItemV2       = (i) => imsQuery('ims_item_save_v2', i);
const receiveInventory = (r) => imsQuery('ims_receive_v2', r);
const removeDamage     = (d) => imsQuery('ims_damage_remove_v2', d);
// Phase 3 (CPX Catch): cycle count sessions. All ims_cc_* calls go through here.
const ccCall           = (q, p = {}) => imsQuery(q, p);
// Phase 4 (CPX Catch): daily deduct + production batches.
const ddCall           = (q, p = {}) => imsQuery(q, p);
const batchCall        = (q, p = {}) => imsQuery(q, p);

// ─────────────────────────────────────────────────────────────────────────────
// SHARED UI
// ─────────────────────────────────────────────────────────────────────────────

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#4a5568', marginBottom: 4 };

function Badge({ children, color = 'gray' }) {
  const colors = {
    green:  { bg: 'rgba(56,161,105,0.12)',  text: '#276749' },
    red:    { bg: 'rgba(229,62,62,0.12)',   text: '#c53030' },
    yellow: { bg: 'rgba(214,158,46,0.14)',  text: '#92600a' },
    blue:   { bg: 'rgba(232,85,10,0.12)',   text: '#c44508' },
    gray:   { bg: '#f0f2f5',                text: '#4a5568' },
  };
  const { bg, text } = colors[color] || colors.gray;
  return (
    <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:99, fontSize:11, fontWeight:700, background:bg, color:text }}>
      {children}
    </span>
  );
}

function LocTag({ code }) {
  return (
    <span style={{ background:'rgba(232,85,10,0.10)', color:'#c44508', fontFamily:'monospace', fontSize:11, fontWeight:700, padding:'2px 7px', borderRadius:4, letterSpacing:'0.05em', border:'1px solid rgba(232,85,10,0.2)' }}>
      {code}
    </span>
  );
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="card" style={{ borderLeft:`4px solid ${accent ? '#e8550a' : '#e2e6ea'}`, padding:'16px 20px' }}>
      <div style={{ fontSize:11, fontWeight:600, color:'#718096', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:800, color:'#1a202c', lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:'#a0aec0', marginTop:4 }}>{sub}</div>}
    </div>
  );
}

function Toast({ msg, type }) {
  if (!msg) return null;
  const bg = type === 'error' ? '#c53030' : '#1a2744';
  return (
    <div style={{ position:'fixed', bottom:24, right:24, background:bg, color:'#fff', borderRadius:8, padding:'12px 18px', fontSize:13, fontWeight:500, boxShadow:'0 8px 24px rgba(0,0,0,0.18)', zIndex:9999, maxWidth:360 }}>
      {msg}
    </div>
  );
}

function EmptyState({ icon, title, body }) {
  return (
    <div style={{ padding:'48px 24px', textAlign:'center', color:'#a0aec0' }}>
      <div style={{ fontSize:32, marginBottom:12 }}>{icon}</div>
      <div style={{ fontSize:15, fontWeight:700, color:'#4a5568', marginBottom:4 }}>{title}</div>
      {body && <div style={{ fontSize:13 }}>{body}</div>}
    </div>
  );
}

// SKU combo — type to filter, or pick from dropdown
function SkuCombo({ items, value, onChange, placeholder }) {
  const [typed, setTyped] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const item = items.find(i => i.id === value);
  const display = item ? `${item.sku} — ${item.desc}` : typed;

  const filtered = typed.length >= 1
    ? items.filter(i => i.sku.toLowerCase().includes(typed.toLowerCase()) || i.desc.toLowerCase().includes(typed.toLowerCase())).slice(0, 20)
    : items.slice(0, 20);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (id) => {
    onChange(id);
    setTyped('');
    setOpen(false);
  };

  const clear = () => { onChange(''); setTyped(''); setOpen(false); };

  // Typing "cp11" and pressing Enter (or tabbing away) picks CP11.
  const pickExact = () => {
    const t = typed.trim().toLowerCase();
    if (!t || value) return false;
    const hit = items.find(i => i.sku.toLowerCase() === t);
    if (hit) { select(hit.id); return true; }
    return false;
  };

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <div style={{ display:'flex', gap:0, border:'1px solid #cbd5e0', borderRadius:6, overflow:'hidden', background:'#fff' }}>
        <input
          className="form-input"
          style={{ border:'none', borderRadius:0, flex:1, fontSize:13 }}
          value={value ? display : typed}
          placeholder={placeholder || '— Type SKU or select —'}
          onChange={e => { setTyped(e.target.value); onChange(''); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={e => { if (e.key === 'Enter') { if (pickExact()) e.preventDefault(); else if (filtered.length === 1) { e.preventDefault(); select(filtered[0].id); } } }}
          onBlur={pickExact}
        />
        {value && (
          <button onClick={clear} style={{ background:'none', border:'none', padding:'0 10px', color:'#a0aec0', cursor:'pointer', fontSize:16 }}>×</button>
        )}
        <button onClick={() => setOpen(o => !o)} style={{ background:'#f4f6f8', border:'none', borderLeft:'1px solid #e2e6ea', padding:'0 12px', cursor:'pointer', color:'#4a5568' }}>▾</button>
      </div>
      {open && filtered.length > 0 && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1px solid #e2e6ea', borderRadius:6, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', zIndex:100, maxHeight:260, overflowY:'auto' }}>
          {filtered.map(i => (
            <div key={i.id} onClick={() => select(i.id)}
              style={{ padding:'8px 12px', cursor:'pointer', fontSize:12, borderBottom:'1px solid #f0f2f5', background: i.id === value ? '#fff7f5' : '#fff' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f4f6f8'}
              onMouseLeave={e => e.currentTarget.style.background = i.id === value ? '#fff7f5' : '#fff'}>
              <span style={{ fontFamily:'monospace', fontWeight:700, color:'#e8550a' }}>{i.sku}</span>
              <span style={{ color:'#4a5568', marginLeft:8 }}>{i.desc}</span>
              {i.active === false && <span style={{ color:'#92600a', marginLeft:8, fontSize:11 }}>(inactive)</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODULE: INVENTORY VIEW
// ─────────────────────────────────────────────────────────────────────────────

function InventoryView({ items, inventory, holds = [], par = {}, onToast }) {
  const [search, setSearch] = useState('');
  const [skuSearch, setSkuSearch] = useState(''); // search by SKU to see all locations
  const [locFilter, setLocFilter] = useState('ALL');
  const [familyFilter, setFamilyFilter] = useState('ALL');

  const itemMap = Object.fromEntries(items.map(i => [i.id, i]));
  const invByItem = {};
  inventory.forEach(r => {
    if (!invByItem[r.item_id]) invByItem[r.item_id] = [];
    invByItem[r.item_id].push(r);
  });

  const allLocs = [...new Set(inventory.map(r => r.loc))].sort();
  const heldAt = {};
  holds.forEach(h => { const k = h.item_id + '|' + h.loc; heldAt[k] = (heldAt[k] || 0) + (h.qty_held || 0); });

  // SKU location lookup mode
  if (skuSearch.trim()) {
    const q = skuSearch.trim().toLowerCase();
    const matchedItems = items.filter(i => i.sku.toLowerCase().includes(q) || i.desc.toLowerCase().includes(q));
    return (
      <div>
        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ padding:'12px 16px', display:'flex', gap:12, alignItems:'center' }}>
            <div style={{ fontWeight:700, fontSize:13, color:'#1a202c' }}>SKU Location Search</div>
            <input className="form-input" type="text" value={skuSearch} onChange={e => setSkuSearch(e.target.value)}
              placeholder="Search by SKU…" style={{ fontSize:12, width:220 }} autoFocus />
            <button className="btn" style={{ fontSize:11 }} onClick={() => setSkuSearch('')}>✕ Clear</button>
          </div>
        </div>
        {matchedItems.map(item => {
          const recs = (invByItem[item.id] || []).filter(r => r.qty > 0);
          return (
            <div key={item.id} className="card" style={{ marginBottom:12 }}>
              <div style={{ padding:'12px 16px', borderBottom:'1px solid #e2e6ea', display:'flex', gap:12, alignItems:'center' }}>
                <span style={{ fontFamily:'monospace', fontWeight:700, fontSize:14, color:'#e8550a' }}>{item.sku}</span>
                <span style={{ fontSize:13, color:'#2d3748' }}>{item.desc}</span>
                <Badge color="gray">{item.family}</Badge>
                <span style={{ marginLeft:'auto', fontSize:12, fontWeight:700, color:'#1a202c' }}>
                  {recs.reduce((s,r) => s+r.qty, 0).toLocaleString()} total units
                </span>
              </div>
              {recs.length === 0
                ? <div style={{ padding:'12px 16px', color:'#a0aec0', fontSize:12 }}>No inventory on hand</div>
                : recs.map(r => (
                  <div key={r.loc} style={{ padding:'8px 16px', display:'flex', gap:16, alignItems:'center', borderBottom:'1px solid #f0f2f5' }}>
                    <LocTag code={r.loc} />
                    <span style={{ fontSize:12, color:'#4a5568' }}>{LOCATIONS[r.loc] || r.loc}</span>
                    <span style={{ marginLeft:'auto', fontFamily:'monospace', fontWeight:700, fontSize:13, color:'#1a202c' }}>{r.qty}</span>
                  </div>
                ))
              }
            </div>
          );
        })}
        {matchedItems.length === 0 && <EmptyState icon="🔍" title="No matches" body={`No SKUs matching "${skuSearch}"`} />}
      </div>
    );
  }

  const rows = items.filter(i => i.active).map(item => {
    const recs = locFilter !== 'ALL'
      ? (invByItem[item.id] || []).filter(r => r.loc === locFilter && r.qty > 0)
      : (invByItem[item.id] || []).filter(r => r.qty > 0);
    const total = recs.reduce((s, r) => s + (r.qty ?? 0), 0);
    return { item, recs, total };
  }).filter(({ item, total }) => {
    if (total === 0) return false; // always hide zero-qty items
    if (familyFilter !== 'ALL' && item.family !== familyFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return item.sku.toLowerCase().includes(q) || item.desc.toLowerCase().includes(q);
    }
    return true;
  });

  const grandTotal = rows.reduce((s, r) => s + r.total, 0);
  const grandValue = rows.reduce((s, r) => s + r.total * (r.item.unit_cost ?? 0), 0);

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
        <StatCard label="Active SKUs" value={items.filter(i=>i.active).length} accent />
        <StatCard label="In Stock" value={rows.length} />
        <StatCard label="Total Units" value={grandTotal.toLocaleString()} />
        <StatCard label="Inventory Value" value={fmtMoney(grandValue)} />
      </div>

      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ padding:'12px 16px', display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
          <input className="form-input" type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search SKU or description…" style={{ fontSize:12, flex:1, minWidth:180, maxWidth:280 }} />
          <button className="btn" onClick={() => setSkuSearch('_')} style={{ fontSize:12 }}>
            🔍 Find SKU Locations
          </button>
          <select className="form-input" value={locFilter} onChange={e => setLocFilter(e.target.value)} style={{ fontSize:12, width:180 }}>
            <option value="ALL">All Locations</option>
            {allLocs.map(l => <option key={l} value={l}>{l} — {LOCATIONS[l]||l}</option>)}
          </select>
          <select className="form-input" value={familyFilter} onChange={e => setFamilyFilter(e.target.value)} style={{ fontSize:12, width:160 }}>
            <option value="ALL">All Families</option>
            {FAMILIES.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <span style={{ fontSize:11, color:'#a0aec0', marginLeft:'auto' }}>{rows.length} items</span>
        </div>
      </div>

      <div className="card">
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>SKU</th><th>Description</th><th>Family</th>
                <th style={{textAlign:'right'}}>On Hand</th>
                <th style={{textAlign:'right'}}>Unit Cost</th>
                <th style={{textAlign:'right'}}>Value</th>
                <th>Locations</th><th style={{textAlign:'right'}}>Par ({getCurrentQuarter().toUpperCase()})</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ item, recs, total }) => {
                const value = total * (item.unit_cost ?? 0);
                const parLevel = parFor(par, item.id);
                const isLow = parLevel != null ? total <= parLevel : total <= 5; // at or below par (Carman, Nov 2026)
                const isAtPar = parLevel != null && total > parLevel && total < parLevel * 1.2;
                return (
                  <tr key={item.id}>
                    <td style={{ fontFamily:'monospace', fontWeight:700, fontSize:12, color:'#1a202c' }}>{item.sku}</td>
                    <td style={{ color:'#2d3748', maxWidth:280 }}>{item.desc}</td>
                    <td><Badge color="gray">{item.family}</Badge></td>
                    <td style={{ textAlign:'right', fontWeight:700, fontSize:14, color: isLow ? '#92600a' : '#276749' }}>{total.toLocaleString()}</td>
                    <td style={{ textAlign:'right', fontFamily:'monospace', fontSize:12, color:'#4a5568' }}>{item.unit_cost != null ? fmtMoney(item.unit_cost) : '—'}</td>
                    <td style={{ textAlign:'right', fontFamily:'monospace', fontSize:12, color:'#4a5568' }}>{value > 0 ? fmtMoney(value) : '—'}</td>
                    <td>
                      <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                        {recs.map(r => (
                          <span key={r.loc} style={{ fontSize:11 }}>
                            <LocTag code={r.loc} />
                            <span style={{ color:'#718096', marginLeft:3 }}>({r.qty})</span>
                            {heldAt[item.id + '|' + r.loc] > 0 && (
                              <span title="Damaged on arrival, waiting on an outcome" style={{ color:'#c53030', fontWeight:700, marginLeft:3 }}>{heldAt[item.id + '|' + r.loc]} on hold</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td style={{ textAlign:'right', fontFamily:'monospace', fontSize:12, color: parLevel != null && total <= parLevel ? '#c53030' : '#a0aec0' }}>
                    {parLevel != null ? parLevel : '—'}
                  </td>
                  <td><Badge color={isLow ? 'yellow' : isAtPar ? 'blue' : 'green'}>
                      {isLow ? `Low${parLevel != null ? ` (par: ${parLevel})` : ''}` : isAtPar ? 'Near Par' : 'In Stock'}
                    </Badge></td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background:'#f4f6f8', fontWeight:700 }}>
                <td colSpan={3} style={{ padding:'10px 12px', color:'#1a202c' }}>Total ({rows.length} items)</td>
                <td style={{ padding:'10px 12px', textAlign:'right', color:'#1a202c', fontSize:14 }}>{grandTotal.toLocaleString()}</td>
                <td />
                <td style={{ padding:'10px 12px', textAlign:'right', color:'#1a202c' }}>{fmtMoney(grandValue)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RECEIVING HELPERS (qty, location, vendor inputs)
// ─────────────────────────────────────────────────────────────────────────────

// Whole-number-only quantity box. Strips anything that isn't a digit as you
// type, so "582.5" can't be entered (Receiving Workflow rule 8).
function QtyInput({ value, onChange, placeholder, disabled, style }) {
  return (
    <input className="form-input" type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="off"
      value={value} disabled={disabled} placeholder={placeholder || '0'} style={style}
      onChange={e => onChange(toWhole(e.target.value))} />
  );
}

// Location box. Type "f01" or "F01", it's stored and shown as F01.
function LocInput({ value, onChange, placeholder, codes }) {
  const listId = useRef('loc-' + uid()).current;
  const v = normLoc(value);
  const known = !v || !!LOCATIONS[v];
  return (
    <div>
      <input className="form-input" list={listId} value={value} autoComplete="off"
        onChange={e => onChange(normLoc(e.target.value))}
        placeholder={placeholder || 'Type a location, e.g. F01'}
        style={{ fontFamily:'monospace', textTransform:'uppercase' }} />
      <datalist id={listId}>
        {(codes || Object.keys(LOCATIONS)).map(k => <option key={k} value={k}>{LOCATIONS[k] || k}</option>)}
      </datalist>
      {v && (
        <div style={{ fontSize:11, marginTop:4, color: known ? '#718096' : '#c53030' }}>
          {known ? LOCATIONS[v] : `${v} isn't a known location`}
        </div>
      )}
    </div>
  );
}

// Vendor picker: pick a saved vendor or type a new one (saved on first receipt).
function VendorPicker({ vendors, vendorId, vendorName, onChange }) {
  const active = vendors.filter(v => v.active !== false);
  const [typing, setTyping] = useState(active.length === 0);
  useEffect(() => { if (active.length === 0) setTyping(true); }, [active.length]);
  if (typing) {
    return (
      <div style={{ display:'flex', gap:8 }}>
        <input className="form-input" type="text" value={vendorName} autoComplete="off" style={{ flex:1 }}
          placeholder="Vendor name" onChange={e => onChange({ vendor_id: null, vendor_name: e.target.value })} />
        {active.length > 0 && (
          <button className="btn" style={{ fontSize:11 }} onClick={() => { setTyping(false); onChange({ vendor_id: null, vendor_name: '' }); }}>Pick saved</button>
        )}
      </div>
    );
  }
  return (
    <select className="form-input" value={vendorId || ''} onChange={e => {
      if (e.target.value === '__new') { setTyping(true); onChange({ vendor_id: null, vendor_name: '' }); return; }
      const v = active.find(x => x.id === e.target.value);
      onChange({ vendor_id: v ? v.id : null, vendor_name: v ? v.name : '' });
    }}>
      <option value="">— Select vendor —</option>
      {active.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
      <option value="__new">+ New vendor…</option>
    </select>
  );
}

// One line in a confirm summary.
function SumRow({ label, children, warn }) {
  return (
    <div style={{ display:'flex', gap:12, padding:'6px 0', borderBottom:'1px solid #f0f2f5', fontSize:13 }}>
      <div style={{ width:150, color:'#718096', flexShrink:0 }}>{label}</div>
      <div style={{ color: warn ? '#c53030' : '#1a202c', fontWeight: warn ? 700 : 500 }}>{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODULE: PUT / RECEIVE
// ─────────────────────────────────────────────────────────────────────────────

const PUT_REASONS = [
  { code:'received_no_po',  label:'Received (no PO)' },
  { code:'customer_return', label:'Customer return / refused delivery' },
  { code:'found_product',   label:'Found product' },
  { code:'other',           label:'Other' },
];
const putReasonLabel = (c) => (PUT_REASONS.find(r => r.code === c) || {}).label || String(c).replace(/_/g, ' ');

function PutInventory({ items, inventory, vendors, onSave, onToast }) {
  const blank = { reason:'received_no_po', itemId:'', loc:'', vendor_id:null, vendor_name:'', lot:'', expected:'', purchaseUnits:'', received:'', overridden:false, damaged:false, damagedQty:'', notes:'' };
  const [f, setF] = useState(blank);
  const [review, setReview] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setF(prev => ({ ...prev, ...patch }));

  // Everything except retired SKUs. Inactive ones come back on receipt.
  const receivable = items.filter(i => !i.retired);
  const item = items.find(i => i.id === f.itemId);
  const upp = item && item.units_per_purchase != null ? Number(item.units_per_purchase) : null;
  const converts = upp != null && upp > 0 && upp !== 1;
  const autoQty = converts && f.purchaseUnits !== '' ? Math.round(Number(f.purchaseUnits) * upp) : null;
  const loc = normLoc(f.loc);
  const locOk = !!LOCATIONS[loc];
  const existing = inventory.find(r => r.item_id === f.itemId && r.loc === loc);
  const receivedNum = f.received === '' ? 0 : Number(f.received);
  const expectedNum = f.expected === '' ? null : Number(f.expected);
  const short = expectedNum != null && expectedNum > receivedNum ? expectedNum - receivedNum : 0;
  const over = expectedNum != null && receivedNum > expectedNum ? receivedNum - expectedNum : 0;
  const damagedNum = f.damaged ? (f.damagedQty === '' ? receivedNum : Number(f.damagedQty)) : 0;
  const damagedBad = f.damaged && (damagedNum <= 0 || damagedNum > receivedNum);

  const pickItem = (id) => {
    const it = items.find(i => i.id === id);
    set({ itemId: id, loc: f.loc || (it && it.home_loc) || '', purchaseUnits:'', received:'', overridden:false });
  };
  const setPurchase = (v) => {
    const auto = v !== '' && converts ? String(Math.round(Number(v) * upp)) : '';
    setF(prev => ({ ...prev, purchaseUnits: v, received: prev.overridden ? prev.received : auto }));
  };

  const problems = [];
  if (!item) problems.push('Pick an item');
  if (!locOk) problems.push('Enter a valid location');
  if (f.damaged && !f.vendor_name.trim()) problems.push('Pick the vendor so they can be credited for the damage');
  if (!(receivedNum > 0)) problems.push('Enter the received qty');
  if (damagedBad) problems.push('Damaged qty has to be between 1 and what was received');
  if (f.reason === 'other' && !f.notes.trim()) problems.push('Add a note saying what this Put is for');
  const ready = problems.length === 0;

  const save = async () => {
    setSaving(true);
    try {
      const r = await receiveInventory({
        id: uid(), item_id: f.itemId, loc, vendor_id: f.vendor_id, vendor_name: f.vendor_name.trim(),
        lot_number: f.lot.trim() || null, expected_qty: f.expected === '' ? null : Number(f.expected),
        received_qty: receivedNum, purchase_units: converts && f.purchaseUnits !== '' ? Number(f.purchaseUnits) : null,
        units_per_purchase: converts ? upp : null, damaged_on_arrival: f.damaged,
        damaged_qty: f.damaged ? damagedNum : null, notes: f.notes.trim() || null, reason_code: f.reason,
      });
      let msg = `Put ${receivedNum} × ${item.sku} → ${loc}`;
      if (r && r.reactivated) msg += ` · ${item.sku} is active again`;
      if (r && r.hold) msg += r.emailSent ? ' · Damage hold set, buyers emailed' : ' · Damage hold set, but the email didn’t send. Let Matt & Matt Sr know.';
      onToast(msg, r && r.hold && !r.emailSent ? 'error' : 'success');
      setF(blank); setReview(false);
      onSave();
    } catch (e) { onToast('Save failed: ' + e.message, 'error'); }
    finally { setSaving(false); }
  };

  if (review && ready) {
    return (
      <div style={{ maxWidth:560 }}>
        <div className="card" style={{ padding:24 }}>
          <h3 style={{ fontSize:15, fontWeight:700, marginBottom:6, color:'#1a202c' }}>Confirm this Put</h3>
          <div style={{ fontSize:15, fontWeight:800, color:'#1a202c', margin:'10px 0 14px' }}>
            Put {receivedNum.toLocaleString()} of {item.sku} into {loc}
          </div>
          <SumRow label="Item">{item.sku} — {item.desc}</SumRow>
          <SumRow label="Location">{locLabel(loc)}</SumRow>
          <SumRow label="On hand there">{existing?.qty ?? 0} → {(existing?.qty ?? 0) + receivedNum}</SumRow>
          {converts && f.purchaseUnits !== '' && (
            <SumRow label="Conversion">{f.purchaseUnits} purchase units × {upp} = {autoQty}{f.overridden && autoQty !== receivedNum ? ` (overridden to ${receivedNum})` : ''}</SumRow>
          )}
          <SumRow label="Vendor">{f.vendor_name.trim() ? `${f.vendor_name.trim()}${!f.vendor_id ? ' (new vendor)' : ''}` : '—'}</SumRow>
          <SumRow label="Lot">{f.lot.trim() || '—'}</SumRow>
          <SumRow label="Expected / received" warn={short > 0}>
            {expectedNum != null ? `${expectedNum} expected, ${receivedNum} received` : `${receivedNum} received`}
            {short > 0 && ` · short ${short}`}{over > 0 && ` · over ${over}`}
          </SumRow>
          <SumRow label="Reason">{putReasonLabel(f.reason)}</SumRow>
          {item.active === false && <SumRow label="Status">{item.sku} is inactive. This receipt makes it active again.</SumRow>}
          {f.damaged && (
            <SumRow label="Damaged on arrival" warn>
              {damagedNum} go on hold. Matt & Matt Sr get an email now. Record the outcome later under Damage.
            </SumRow>
          )}
          {f.notes.trim() && <SumRow label="Notes">{f.notes.trim()}</SumRow>}
          <div style={{ display:'flex', gap:8, marginTop:18 }}>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Confirm & save'}</button>
            <button className="btn" onClick={() => setReview(false)} disabled={saving}>Back</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth:560 }}>
      <div className="card" style={{ padding:24 }}>
        <h3 style={{ fontSize:15, fontWeight:700, marginBottom:20, color:'#1a202c' }}>Receive Inventory (Put)</h3>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div>
            <label style={labelStyle}>Reason *</label>
            <select className="form-input" value={f.reason} onChange={e => set({ reason: e.target.value, damaged: e.target.value === 'received_no_po' ? f.damaged : false })}>
              {PUT_REASONS.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
            {f.reason === 'other' && <div style={{ fontSize:11, color:'#92600a', marginTop:4 }}>Other needs a note below saying what this is.</div>}
          </div>
          <div>
            <label style={labelStyle}>Item (type SKU or select)</label>
            <SkuCombo items={receivable} value={f.itemId} onChange={pickItem} />
            {item && item.active === false && (
              <div style={{ fontSize:11, color:'#92600a', marginTop:4 }}>Inactive SKU. Receiving it makes it active again.</div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Location</label>
            <LocInput value={f.loc} onChange={v => set({ loc: v })} />
            {item && item.home_loc && f.loc === item.home_loc && (
              <div style={{ fontSize:11, color:'#a0aec0', marginTop:2 }}>Filled in from this SKU’s home location. Change it if it’s going somewhere else.</div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Vendor {f.damaged ? '*' : '(optional)'}</label>
            <VendorPicker vendors={vendors} vendorId={f.vendor_id} vendorName={f.vendor_name} onChange={v => set(v)} />
          </div>
          <div>
            <label style={labelStyle}>Lot number (optional)</label>
            <input className="form-input" type="text" value={f.lot} onChange={e => set({ lot: e.target.value })} placeholder="Leave blank if there isn’t one" />
          </div>
          {converts && (
            <div>
              <label style={labelStyle}>Purchase units received</label>
              <QtyInput value={f.purchaseUnits} onChange={setPurchase} />
              <div style={{ fontSize:11, color:'#718096', marginTop:4 }}>
                {upp} selling units per purchase unit{autoQty != null ? ` → ${autoQty} selling units` : ''}
              </div>
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={labelStyle}>Expected qty</label>
              <QtyInput value={f.expected} onChange={v => set({ expected: v })} placeholder="Optional" />
            </div>
            <div>
              <label style={labelStyle}>Received qty *</label>
              <QtyInput value={f.received} onChange={v => set({ received: v, overridden: converts && v !== '' && String(autoQty ?? '') !== v })} />
            </div>
          </div>
          {converts && f.overridden && autoQty != null && (
            <div style={{ fontSize:11, color:'#92600a', marginTop:-8 }}>
              Overridden. Auto count was {autoQty}.{' '}
              <a style={{ cursor:'pointer', textDecoration:'underline' }} onClick={() => set({ received: String(autoQty), overridden:false })}>Use {autoQty}</a>
            </div>
          )}
          {(short > 0 || over > 0) && (
            <div style={{ fontSize:12, color: short > 0 ? '#c53030' : '#92600a', marginTop:-8 }}>
              {short > 0 ? `Short ${short}. Only the ${receivedNum} received gets added; the shortage is kept on file.` : `${over} more than expected.`}
            </div>
          )}
          {existing && receivedNum > 0 && (
            <div style={{ fontSize:11, color:'#718096', marginTop:-8 }}>
              Current at {loc}: <strong>{existing.qty}</strong> → will become <strong>{existing.qty + receivedNum}</strong>
            </div>
          )}
          {f.reason === 'received_no_po' && <div style={{ padding:12, borderRadius:8, background: f.damaged ? 'rgba(229,62,62,0.06)' : '#f8f9fb', border:`1px solid ${f.damaged ? 'rgba(229,62,62,0.25)' : '#e2e6ea'}` }}>
            <label style={{ ...labelStyle, display:'flex', alignItems:'center', gap:8, cursor:'pointer', marginBottom:0 }}>
              <input type="checkbox" checked={f.damaged} onChange={e => set({ damaged: e.target.checked, damagedQty:'' })} />
              Damaged on arrival
            </label>
            {f.damaged && (
              <div style={{ marginTop:10 }}>
                <label style={labelStyle}>How many are damaged?</label>
                <QtyInput value={f.damagedQty} onChange={v => set({ damagedQty: v })} placeholder={receivedNum ? String(receivedNum) : 'All'} style={{ maxWidth:160 }} />
                <div style={{ fontSize:11, color:'#718096', marginTop:4 }}>
                  They go on hold and Matt & Matt Sr get an email. Record what happened later under Damage.
                </div>
              </div>
            )}
          </div>}
          <div>
            <label style={labelStyle}>{f.reason === 'other' ? 'Notes *' : 'Notes (optional)'}</label>
            <input className="form-input" type="text" value={f.notes} onChange={e => set({ notes: e.target.value })}
              placeholder={f.reason === 'other' ? 'What is this Put for?' : ''} />
          </div>
          {!ready && (f.itemId || f.received) && (
            <div style={{ fontSize:11, color:'#a0aec0' }}>{problems.join(' · ')}</div>
          )}
          <button className="btn btn-primary" onClick={() => setReview(true)} disabled={!ready} style={{ alignSelf:'flex-start' }}>
            Review {receivedNum > 0 ? receivedNum : '—'} units
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODULE: DAMAGE (open holds + Remove as Damage)
// ─────────────────────────────────────────────────────────────────────────────

const DAMAGE_REASONS = [
  'Damaged in transit', 'Crushed / broken packaging', 'Thawed / temperature', 'Quality / spoiled', 'Processing loss', 'Other',
];
// Phase 5: the same screen logs Waste and Quality Test pulls. Each kind is its own
// line in the Owner Summary's documented losses. Only Damage needs a vendor.
const REMOVAL_KINDS = {
  damage:  { label: 'Damage',       verb: 'as damage',          reasons: DAMAGE_REASONS },
  waste:   { label: 'Waste',        verb: 'as waste',           reasons: ['Expired', 'Spoiled', 'Dropped / mishandled', 'Over-production', 'Other'] },
  quality: { label: 'Quality Test', verb: 'for a quality test', reasons: ['Taste test', 'Weight / size check', 'Checking a complaint', 'Other'] },
};

// Shared form for "record outcome" (with a hold) and plain "Remove as Damage".
function DamageForm({ items, inventory, vendors, hold, kind = 'damage', onDone, onCancel, onToast }) {
  const K = REMOVAL_KINDS[hold ? 'damage' : kind] || REMOVAL_KINDS.damage;
  const needsVendor = !!hold || kind === 'damage';
  const [itemId, setItemId] = useState(hold ? hold.item_id : '');
  const [outcome, setOutcome] = useState(hold ? '' : 'loss');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  const [vendor, setVendor] = useState({ vendor_id: hold ? hold.vendor_id : null, vendor_name: hold ? (hold.vendor_name || '') : '' });
  const [notes, setNotes] = useState('');
  const [review, setReview] = useState(false);
  const [saving, setSaving] = useState(false);

  const item = items.find(i => i.id === itemId);
  const stockLocs = inventory.filter(r => r.item_id === itemId && r.qty > 0);
  // Only pre-fill the location when the product sits in exactly one place.
  const [loc, setLoc] = useState('');
  useEffect(() => { setLoc(stockLocs.length === 1 ? stockLocs[0].loc : ''); }, [itemId, stockLocs.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (hold && outcome === 'total') setQty(String(hold.qty_held)); }, [outcome]); // eslint-disable-line react-hooks/exhaustive-deps

  const removing = outcome !== 'fine';
  const have = inventory.find(r => r.item_id === itemId && r.loc === loc)?.qty ?? 0;
  const qtyNum = qty === '' ? 0 : Number(qty);
  const problems = [];
  if (hold && !outcome) problems.push('Pick an outcome');
  if (removing) {
    if (!item) problems.push('Pick an item');
    if (!loc) problems.push('Pick the location it’s coming out of');
    if (!(qtyNum > 0)) problems.push('Enter the qty');
    if (qtyNum > have) problems.push(`Only ${have} at ${loc || 'that location'}`);
    if (!reason) problems.push('Pick a reason');
    if (needsVendor && !vendor.vendor_name.trim()) problems.push('Pick the vendor');
  }
  const ready = problems.length === 0;

  const save = async () => {
    setSaving(true);
    try {
      const r = await removeDamage({
        hold_id: hold ? hold.id : null, outcome: hold ? outcome : null, item_id: itemId, kind: hold ? 'damage' : kind,
        qty: removing ? qtyNum : null, loc: removing ? loc : null, reason_code: removing ? reason : null,
        vendor_name: vendor.vendor_name.trim(), notes: notes.trim() || null,
      });
      const what = removing ? `Removed ${qtyNum} × ${item?.sku} from ${loc} ${K.verb}` : `${item?.sku} hold closed, no loss`;
      onToast(hold ? `${what}${r && r.emailSent ? ' · buyers emailed' : ' · email didn’t send'}` : what, hold && !(r && r.emailSent) ? 'error' : 'success');
      onDone();
    } catch (e) { onToast('Save failed: ' + e.message, 'error'); }
    finally { setSaving(false); }
  };

  if (review && ready) {
    const outcomeLabel = { fine:'Fine, no loss', partial:'Partial loss', total:'Total loss' }[outcome];
    return (
      <div className="card" style={{ padding:24, maxWidth:560 }}>
        <h3 style={{ fontSize:15, fontWeight:700, marginBottom:12, color:'#1a202c' }}>Confirm</h3>
        <div style={{ fontSize:15, fontWeight:800, color:'#1a202c', marginBottom:12 }}>
          {removing ? `Remove ${qtyNum} of ${item.sku} from ${loc} ${K.verb}` : `Close the hold on ${item?.sku}, nothing removed`}
        </div>
        {hold && <SumRow label="Outcome">{outcomeLabel}</SumRow>}
        {item && <SumRow label="Item">{item.sku} — {item.desc}</SumRow>}
        {removing && <SumRow label="Location">{locLabel(loc)} ({have} → {have - qtyNum})</SumRow>}
        {removing && <SumRow label="Reason">{reason}</SumRow>}
        {needsVendor && <SumRow label="Vendor">{vendor.vendor_name.trim() || '—'}</SumRow>}
        {notes.trim() && <SumRow label="Notes">{notes.trim()}</SumRow>}
        {hold && <SumRow label="Email">Matt & Matt Sr get the result for the vendor credit</SumRow>}
        <div style={{ display:'flex', gap:8, marginTop:18 }}>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={removing ? { background:'#c53030', borderColor:'#c53030' } : undefined}>
            {saving ? 'Saving…' : 'Confirm & save'}
          </button>
          <button className="btn" onClick={() => setReview(false)} disabled={saving}>Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding:24, maxWidth:560 }}>
      <h3 style={{ fontSize:15, fontWeight:700, marginBottom:16, color:'#1a202c' }}>
        {hold ? `Record outcome: ${item?.sku || ''}` : `Remove as ${K.label}`}
      </h3>
      <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
        {hold ? (
          <div style={{ fontSize:12, color:'#4a5568' }}>
            {hold.qty_held} held from {hold.vendor_name || 'vendor'}{hold.lot_number ? ` · Lot ${hold.lot_number}` : ''} · received {fmtDate(hold.created_at)} into {hold.loc}
          </div>
        ) : (
          <div>
            <label style={labelStyle}>Item</label>
            <SkuCombo items={items.filter(i => !i.retired)} value={itemId} onChange={setItemId} />
          </div>
        )}
        {hold && (
          <div>
            <label style={labelStyle}>Outcome</label>
            <div style={{ display:'flex', gap:8 }}>
              {[['fine','Fine'],['partial','Partial loss'],['total','Total loss']].map(([k, l]) => (
                <button key={k} className={`btn ${outcome === k ? 'btn-primary' : ''}`} style={{ fontSize:12 }} onClick={() => setOutcome(k)}>{l}</button>
              ))}
            </div>
          </div>
        )}
        {removing && outcome && (
          <>
            <div>
              <label style={labelStyle}>Take it out of</label>
              <select className="form-input" value={loc} onChange={e => setLoc(e.target.value)}>
                <option value="">— Pick location —</option>
                {stockLocs.map(r => <option key={r.loc} value={r.loc}>{r.loc} — {LOCATIONS[r.loc] || r.loc} ({r.qty})</option>)}
              </select>
              {itemId && stockLocs.length === 0 && <div style={{ fontSize:11, color:'#c53030', marginTop:4 }}>None of this item is on hand anywhere.</div>}
            </div>
            <div>
              <label style={labelStyle}>Qty to remove</label>
              <QtyInput value={qty} onChange={setQty} style={{ maxWidth:160 }} />
            </div>
            <div>
              <label style={labelStyle}>Reason</label>
              <select className="form-input" value={reason} onChange={e => setReason(e.target.value)}>
                <option value="">— Select reason —</option>
                {K.reasons.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            {needsVendor && (
              <div>
                <label style={labelStyle}>Vendor</label>
                <VendorPicker vendors={vendors} vendorId={vendor.vendor_id} vendorName={vendor.vendor_name} onChange={setVendor} />
              </div>
            )}
          </>
        )}
        <div>
          <label style={labelStyle}>Notes (optional)</label>
          <input className="form-input" type="text" value={notes} onChange={e => setNotes(e.target.value)} />
        </div>
        {!ready && <div style={{ fontSize:11, color:'#a0aec0' }}>{problems.join(' · ')}</div>}
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-primary" onClick={() => setReview(true)} disabled={!ready}>Review</button>
          {onCancel && <button className="btn" onClick={onCancel}>Cancel</button>}
        </div>
      </div>
    </div>
  );
}

function DamageModule({ items, inventory, vendors, holds, onSave, onToast }) {
  const [mode, setMode] = useState(null); // null | 'damage' | 'waste' | 'quality' | hold object
  const itemMap = Object.fromEntries(items.map(i => [i.id, i]));
  const done = () => { setMode(null); onSave(); };

  if (mode) {
    return (
      <DamageForm key={typeof mode === 'string' ? mode : mode.id} items={items} inventory={inventory} vendors={vendors}
        hold={typeof mode === 'string' ? null : mode} kind={typeof mode === 'string' ? mode : 'damage'}
        onDone={done} onCancel={() => setMode(null)} onToast={onToast} />
    );
  }

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
        <h3 style={{ fontSize:15, fontWeight:700, color:'#1a202c', margin:0 }}>Damage holds</h3>
        <span style={{ fontSize:11, color:'#a0aec0' }}>{holds.length} open</span>
        <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
          <button className="btn btn-primary" style={{ fontSize:12 }} onClick={() => setMode('damage')}>Remove as Damage</button>
          <button className="btn" style={{ fontSize:12 }} onClick={() => setMode('waste')}>Remove as Waste</button>
          <button className="btn" style={{ fontSize:12 }} onClick={() => setMode('quality')}>Quality Test</button>
        </div>
      </div>
      <div className="card">
        {holds.length === 0 ? (
          <EmptyState icon="✅" title="No open damage holds" body="Anything checked “Damaged on arrival” shows up here until its outcome is recorded." />
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>Received</th><th>SKU</th><th>Location</th><th style={{textAlign:'right'}}>On hold</th><th>Vendor</th><th>Lot</th><th>Emailed</th><th></th></tr></thead>
              <tbody>
                {holds.map(h => (
                  <tr key={h.id}>
                    <td style={{ fontSize:11, color:'#718096', whiteSpace:'nowrap' }}>{fmtDate(h.created_at)}</td>
                    <td style={{ fontFamily:'monospace', fontWeight:700, fontSize:12 }}>{itemMap[h.item_id]?.sku ?? h.item_id}</td>
                    <td><LocTag code={h.loc} /></td>
                    <td style={{ textAlign:'right', fontWeight:700, color:'#c53030' }}>{h.qty_held}</td>
                    <td style={{ fontSize:12 }}>{h.vendor_name || '—'}</td>
                    <td style={{ fontSize:12 }}>{h.lot_number || '—'}</td>
                    <td>{h.notified_at ? <Badge color="green">Yes</Badge> : <Badge color="red">No</Badge>}</td>
                    <td><button className="btn" style={{ fontSize:11, padding:'3px 10px' }} onClick={() => setMode(h)}>Record outcome</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODULE: MOVE INVENTORY
// ─────────────────────────────────────────────────────────────────────────────

function MoveInventory({ items, inventory, onSave, onToast }) {
  const [itemId, setItemId] = useState('');
  const [fromLoc, setFromLoc] = useState('');
  const [toLoc, setToLoc] = useState('');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastMove, setLastMove] = useState(null); // { itemId, sku, desc, from, to, qty }

  const item = items.find(i => i.id === itemId);
  const fromRec = inventory.find(r => r.item_id === itemId && r.loc === fromLoc);
  const available = fromRec?.qty ?? 0;
  const qtyNum = Number(qty || 0);
  const overStock = qtyNum > available;
  const locsWithItem = [...new Set(inventory.filter(r => r.item_id === itemId && r.qty > 0).map(r => r.loc))].sort();

  const execute = async () => {
    if (!itemId || !fromLoc || !toLoc || qtyNum <= 0 || overStock || !reason) return;
    setSaving(true);
    try {
      const newFrom = available - qtyNum;
      const toRec = inventory.find(r => r.item_id === itemId && r.loc === toLoc);
      const newTo = (toRec?.qty ?? 0) + qtyNum;
      if (newFrom === 0) await deleteInventoryRecord(fromLoc, itemId);
      else await saveInventoryRecord({ item_id:itemId, loc:fromLoc, qty:newFrom, last_updated:dateStr() });
      await saveInventoryRecord({ item_id:itemId, loc:toLoc, qty:newTo, last_updated:dateStr() });
      await appendAuditEntry({ module:'Manage Inventory', action:'Move', item_id:itemId, loc:fromLoc, qty:qtyNum, notes:`→ ${toLoc}${notes?': '+notes:''}`, reason_code:reason, created_at:dateStr() });
      onToast(`Moved ${qtyNum} × ${item?.sku}: ${fromLoc} → ${toLoc}`, 'success');
      setLastMove({ itemId, sku: item?.sku, desc: item?.desc, from: fromLoc, to: toLoc, qty: qtyNum });
      await onSave();
      setItemId(''); setFromLoc(''); setToLoc(''); setQty(''); setReason(''); setNotes('');
    } catch (e) { onToast('Move failed: ' + e.message, 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth:560 }}>
      <div className="card" style={{ padding:24 }}>
        <h3 style={{ fontSize:15, fontWeight:700, marginBottom:20, color:'#1a202c' }}>Move Between Locations</h3>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div>
            <label style={labelStyle}>Item (type SKU or select)</label>
            <SkuCombo items={items.filter(i=>i.active)} value={itemId} onChange={id => { setItemId(id); setFromLoc(''); }} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={labelStyle}>From</label>
              <select className="form-input" value={fromLoc} onChange={e => setFromLoc(e.target.value)}>
                <option value="">— From —</option>
                {locsWithItem.map(l => <option key={l} value={l}>{l} ({inventory.find(r=>r.item_id===itemId&&r.loc===l)?.qty??0})</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>To</label>
              <select className="form-input" value={toLoc} onChange={e => setToLoc(e.target.value)}>
                <option value="">— To —</option>
                {Object.entries(LOCATIONS).filter(([k])=>k!==fromLoc).map(([k,v])=><option key={k} value={k}>{k} — {v}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Quantity</label>
            <input className="form-input" type="number" min="1" max={available} value={qty} onChange={e => setQty(e.target.value)} placeholder="0" />
            {fromLoc && (
              <div style={{ fontSize:11, marginTop:4, color: overStock ? '#c53030' : '#718096' }}>
                Available at {fromLoc}: <strong>{available}</strong>{overStock && ' — exceeds available'}
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Reason *</label>
            <select className="form-input" value={reason} onChange={e => setReason(e.target.value)}>
              <option value="">— Select reason —</option>
              {REASON_CODES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Notes (optional)</label>
            <input className="form-input" type="text" value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
          <button className="btn btn-primary" onClick={execute} disabled={!itemId||!fromLoc||!toLoc||qtyNum<=0||overStock||!reason||saving} style={{ alignSelf:'flex-start' }}>
            {saving ? 'Moving…' : `Move ${qty||'—'} units`}
          </button>
        </div>
      </div>
      {lastMove && (() => {
        // After a move, show every location the SKU is in now so the move can be checked.
        const rows = inventory.filter(r => r.item_id === lastMove.itemId && (r.qty > 0 || r.loc === lastMove.to)).sort((a, b) => a.loc.localeCompare(b.loc));
        const total = rows.reduce((t, r) => t + Number(r.qty || 0), 0);
        return (
          <div className="card" style={{ padding:20, marginTop:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:4 }}>
              <h3 style={{ fontSize:14, fontWeight:700, color:'#1a202c' }}>Where {lastMove.sku} is now</h3>
              <a style={{ fontSize:11, color:'#a0aec0', cursor:'pointer' }} onClick={() => setLastMove(null)}>Hide</a>
            </div>
            <div style={{ fontSize:12, color:'#718096', marginBottom:10 }}>Just moved {lastMove.qty} from {lastMove.from} to {lastMove.to}.{lastMove.desc ? ` ${lastMove.desc}.` : ''}</div>
            {rows.length === 0 ? <div style={{ fontSize:12, color:'#a0aec0' }}>Not on hand anywhere.</div> : (
              <table style={{ fontSize:13 }}>
                <thead><tr><th>Location</th><th style={{ textAlign:'right' }}>On hand</th><th></th></tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.loc}>
                      <td><LocTag code={r.loc} /> <span style={{ fontSize:11, color:'#718096' }}>{LOCATIONS[r.loc] || ''}</span></td>
                      <td style={{ textAlign:'right', fontFamily:'monospace', fontWeight:700 }}>{r.qty}</td>
                      <td style={{ fontSize:11, color:'#718096' }}>{r.loc === lastMove.to ? 'moved in' : r.loc === lastMove.from ? 'moved out' : ''}</td>
                    </tr>
                  ))}
                  <tr><td style={{ fontWeight:700 }}>Total</td><td style={{ textAlign:'right', fontFamily:'monospace', fontWeight:700 }}>{total}</td><td></td></tr>
                </tbody>
              </table>
            )}
            {!rows.some(r => r.loc === lastMove.from) && <div style={{ fontSize:11, color:'#718096', marginTop:8 }}>{lastMove.from} is now empty for this SKU.</div>}
          </div>
        );
      })()}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODULE: ITEMS (add / edit / remove SKUs)
// ─────────────────────────────────────────────────────────────────────────────

// Admin-only cost history for one SKU.
function CostHistory({ itemId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let live = true;
    loadCostLog(itemId).then(r => { if (live) setRows(r); }).catch(() => { if (live) setRows([]); });
    return () => { live = false; };
  }, [itemId]);
  return (
    <div style={{ marginTop:20 }}>
      <div style={{ fontSize:12, fontWeight:700, color:'#4a5568', marginBottom:6 }}>Cost change log</div>
      {rows === null ? <div style={{ fontSize:12, color:'#a0aec0' }}>Loading…</div>
        : rows.length === 0 ? <div style={{ fontSize:12, color:'#a0aec0' }}>No cost changes logged yet.</div>
        : (
          <table style={{ fontSize:12 }}>
            <thead><tr><th>When</th><th style={{textAlign:'right'}}>Old</th><th style={{textAlign:'right'}}>New</th><th>Who</th><th>Notes</th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={{ whiteSpace:'nowrap', color:'#718096' }}>{fmtDate(r.created_at)}</td>
                  <td style={{ textAlign:'right', fontFamily:'monospace' }}>{r.old_cost != null ? fmtMoney(r.old_cost) : '—'}</td>
                  <td style={{ textAlign:'right', fontFamily:'monospace', fontWeight:700 }}>{r.new_cost != null ? fmtMoney(r.new_cost) : '—'}</td>
                  <td>{r.changed_by || '—'}</td>
                  <td style={{ color:'#718096' }}>{r.notes || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  );
}

const itemStatus = (i) => i.retired ? 'retired' : i.active ? 'active' : 'inactive';
const STATUS_BADGE = { active:['green','Active'], inactive:['yellow','Inactive'], retired:['gray','Retired'] };

function ItemsModule({ items, par = {}, isAdmin, onSave, onToast }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('current'); // current | active | inactive | retired | all
  const [editing, setEditing] = useState(null); // null | 'new' | item object
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const [retireReason, setRetireReason] = useState('');

  const filtered = items.filter(i => {
    const st = itemStatus(i);
    if (statusFilter === 'current' && st === 'retired') return false;
    if (!['current', 'all'].includes(statusFilter) && st !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return i.sku.toLowerCase().includes(q) || i.desc.toLowerCase().includes(q) || (i.family||'').toLowerCase().includes(q);
  });

  const openNew = () => {
    setForm({ id: uid(), sku:'', desc:'', family:'', active:true, unit_cost:'', units_per_purchase:'', home_loc:'', q1:'', q2:'', q3:'', q4:'' });
    setRetiring(false); setRetireReason('');
    setEditing('new');
  };

  const openEdit = (item) => {
    setForm({
      ...item,
      unit_cost: item.unit_cost != null ? String(Number(item.unit_cost)) : '',
      units_per_purchase: item.units_per_purchase != null ? String(Number(item.units_per_purchase)) : '',
      home_loc: item.home_loc || '',
      ...Object.fromEntries(PAR_QS.map(q => [q, par[item.id] && par[item.id][q] != null ? String(par[item.id][q]) : ''])),
    });
    setRetiring(false); setRetireReason('');
    setEditing(item);
  };

  const readOnly = editing && editing !== 'new' && editing.retired;
  const homeOk = !form.home_loc || !!LOCATIONS[normLoc(form.home_loc)];
  const uppOk = form.units_per_purchase === '' || Number(form.units_per_purchase) > 0;
  const parOk = PAR_QS.every(q => form[q] == null || form[q] === '' || (Number.isInteger(Number(form[q])) && Number(form[q]) >= 0));
  const canSave = form.sku?.trim() && form.desc?.trim() && homeOk && uppOk && parOk && !saving;

  const save = async (extra = {}) => {
    if (!canSave) return;
    setSaving(true);
    try {
      const itemFields = Object.fromEntries(Object.entries(form).filter(([k]) => !PAR_QS.includes(k)));
      const r = await saveItemV2({
        ...itemFields, ...extra,
        sku: form.sku.trim().toUpperCase(), desc: form.desc.trim(),
        unit_cost: form.unit_cost === '' ? null : Number(form.unit_cost),
        units_per_purchase: form.units_per_purchase === '' ? null : Number(form.units_per_purchase),
        home_loc: normLoc(form.home_loc) || null,
      });
      const sku = form.sku.trim().toUpperCase();
      let parNote = '';
      if (!extra.retired) {
        const before = par[form.id] || {};
        const parChanged = PAR_QS.some(q => (before[q] == null ? '' : String(before[q])) !== String(form[q] ?? '').trim());
        if (parChanged) {
          try { await savePar({ item_id: form.id, ...Object.fromEntries(PAR_QS.map(q => [q, String(form[q] ?? '').trim() === '' ? null : Number(form[q])])) }); parNote = ' · par updated'; }
          catch (e) { onToast(`${sku} saved, but par didn’t save: ${e.message}`, 'error'); setEditing(null); onSave(); return; }
        }
      }
      onToast(extra.retired ? `${sku} retired` : editing === 'new' ? `${sku} added` : `${sku} updated${r && r.costLogged ? ' · cost change logged' : ''}${parNote}`, 'success');
      setEditing(null);
      onSave();
    } catch (e) { onToast('Save failed: ' + e.message, 'error'); }
    finally { setSaving(false); }
  };

  if (editing) {
    const st = editing === 'new' ? 'active' : itemStatus(editing);
    return (
      <div style={{ maxWidth:600 }}>
        <div className="card" style={{ padding:24 }}>
          <h3 style={{ fontSize:15, fontWeight:700, marginBottom:20, color:'#1a202c', display:'flex', gap:10, alignItems:'center' }}>
            {editing === 'new' ? 'Add New SKU' : readOnly ? form.sku : `Edit ${form.sku}`}
            {editing !== 'new' && <Badge color={STATUS_BADGE[st][0]}>{STATUS_BADGE[st][1]}</Badge>}
          </h3>
          {readOnly && (
            <div style={{ padding:'10px 12px', marginBottom:16, borderRadius:8, background:'#f4f6f8', fontSize:12, color:'#4a5568' }}>
              Retired{editing.retire_reason ? `: ${editing.retire_reason}` : ''}. Retired SKUs are permanent and can’t be edited or received. Its history stays.
            </div>
          )}
          <fieldset disabled={readOnly || saving} style={{ border:'none', padding:0, margin:0 }}>
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:12 }}>
                <div>
                  <label style={labelStyle}>SKU *</label>
                  <input className="form-input" type="text" value={form.sku} onChange={e => setForm(f=>({...f, sku:e.target.value.toUpperCase()}))} placeholder="CP001" style={{ fontFamily:'monospace', textTransform:'uppercase' }} />
                </div>
                <div>
                  <label style={labelStyle}>Description *</label>
                  <input className="form-input" type="text" value={form.desc} onChange={e => setForm(f=>({...f, desc:e.target.value}))} placeholder="Product description" />
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={labelStyle}>Family</label>
                  <select className="form-input" value={form.family||''} onChange={e => setForm(f=>({...f, family:e.target.value}))}>
                    <option value="">— Select —</option>
                    {FAMILIES.map(fam => <option key={fam} value={fam}>{fam}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Unit Cost ($)</label>
                  <input className="form-input" type="number" min="0" step="0.01" value={form.unit_cost}
                    onChange={e => setForm(f=>({...f, unit_cost:e.target.value}))} placeholder="0.00" />
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={labelStyle}>Selling units per purchase unit</label>
                  <input className="form-input" type="number" min="0" step="any" value={form.units_per_purchase}
                    onChange={e => setForm(f=>({...f, units_per_purchase:e.target.value}))} placeholder="Blank = 1 to 1" />
                  <div style={{ fontSize:11, color: uppOk ? '#a0aec0' : '#c53030', marginTop:4 }}>
                    {uppOk ? 'e.g. 9 two-packs per 10 lb box. Put converts with this.' : 'Has to be above 0'}
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Home location</label>
                  <LocInput value={form.home_loc} onChange={v => setForm(f=>({...f, home_loc:v}))} placeholder="e.g. F01" />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Par level by quarter</label>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8 }}>
                  {PAR_QS.map(q => (
                    <div key={q}>
                      <div style={{ fontSize:10, fontWeight:700, color: q === getCurrentQuarter() ? '#e8550a' : '#a0aec0', marginBottom:2 }}>
                        {q.toUpperCase()}{q === getCurrentQuarter() ? ' (now)' : ''}
                      </div>
                      <input className="form-input" type="number" min="0" step="1" value={form[q] ?? ''}
                        onChange={e => setForm(f=>({...f, [q]:e.target.value}))} placeholder="—" />
                    </div>
                  ))}
                </div>
                <div style={{ fontSize:11, color: parOk ? '#a0aec0' : '#c53030', marginTop:4 }}>
                  {parOk ? 'Q1 Jan–Mar · Q2 Apr–Jun · Q3 Jul–Sep · Q4 Oct–Dec. Leave blank for no par. The inventory view flags it Low when it drops under this quarter’s par.' : 'Par has to be a whole number, 0 or more'}
                </div>
              </div>
              {!readOnly && (
                <div>
                  <label style={labelStyle}>Status</label>
                  <div style={{ display:'flex', gap:8 }}>
                    <button type="button" className={`btn ${form.active ? 'btn-primary' : ''}`} style={{ fontSize:12 }} onClick={() => setForm(f=>({...f, active:true}))}>Active</button>
                    <button type="button" className={`btn ${!form.active ? 'btn-primary' : ''}`} style={{ fontSize:12 }} onClick={() => setForm(f=>({...f, active:false}))}>Inactive</button>
                  </div>
                  <div style={{ fontSize:11, color:'#a0aec0', marginTop:4 }}>
                    Inactive hides it from lists but keeps the SKU and its history. It turns back on by itself the next time it’s received.
                  </div>
                </div>
              )}
            </div>
          </fieldset>

          {!readOnly && !retiring && (
            <div style={{ display:'flex', gap:8, marginTop:18 }}>
              <button className="btn btn-primary" onClick={() => save()} disabled={!canSave}>{saving ? 'Saving…' : 'Save'}</button>
              <button className="btn" onClick={() => setEditing(null)}>Cancel</button>
              {editing !== 'new' && (
                <button className="btn" style={{ marginLeft:'auto', color:'#c53030' }} onClick={() => setRetiring(true)}>Retire SKU…</button>
              )}
            </div>
          )}
          {readOnly && (
            <div style={{ marginTop:18 }}><button className="btn" onClick={() => setEditing(null)}>Back</button></div>
          )}

          {retiring && (
            <div style={{ marginTop:18, padding:'14px 16px', borderRadius:8, background:'rgba(229,62,62,0.06)', border:'1px solid rgba(229,62,62,0.25)' }}>
              <div style={{ fontSize:13, color:'#c53030', fontWeight:700, marginBottom:6 }}>Retire {form.sku}? This is permanent.</div>
              <div style={{ fontSize:12, color:'#4a5568', marginBottom:10 }}>
                It can’t be received or edited again. Its history stays. If you might use it again, set it to Inactive instead.
              </div>
              <label style={labelStyle}>Reason *</label>
              <input className="form-input" type="text" value={retireReason} onChange={e => setRetireReason(e.target.value)} placeholder="e.g. Discontinued by vendor" />
              <div style={{ display:'flex', gap:8, marginTop:10 }}>
                <button className="btn btn-primary" style={{ background:'#c53030', borderColor:'#c53030' }}
                  disabled={!retireReason.trim() || !canSave} onClick={() => save({ retired:true, retire_reason:retireReason.trim(), active:false })}>
                  {saving ? 'Saving…' : 'Retire permanently'}
                </button>
                <button className="btn" onClick={() => setRetiring(false)}>Never mind</button>
              </div>
            </div>
          )}

          {editing !== 'new' && <CostHistory itemId={editing.id} />}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display:'flex', gap:12, alignItems:'center', marginBottom:16 }}>
        <input className="form-input" type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search SKU, description, or family…" style={{ fontSize:12, flex:1, maxWidth:320 }} />
        <select className="form-input" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ fontSize:12, width:170 }}>
          <option value="current">Active + Inactive</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="retired">Retired</option>
          <option value="all">All</option>
        </select>
        <button className="btn btn-primary" onClick={openNew} style={{ fontSize:12 }}>+ Add SKU</button>
        <span style={{ fontSize:11, color:'#a0aec0' }}>{filtered.length} items</span>
      </div>

      <div className="card">
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>SKU</th><th>Description</th><th>Family</th>
                <th style={{textAlign:'right'}}>Unit Cost</th>
                <th style={{textAlign:'right'}}>Units / Purchase</th>
                <th style={{textAlign:'right'}}>Par ({getCurrentQuarter().toUpperCase()})</th>
                <th>Home</th>
                <th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {[...filtered].sort((a,b) => a.sku.localeCompare(b.sku)).map(item => {
                const st = itemStatus(item);
                return (
                  <tr key={item.id} style={st === 'retired' ? { opacity:0.6 } : undefined}>
                    <td style={{ fontFamily:'monospace', fontWeight:700, fontSize:12, color:'#1a202c' }}>{item.sku}</td>
                    <td style={{ color:'#2d3748' }}>{item.desc}</td>
                    <td><Badge color="gray">{item.family||'—'}</Badge></td>
                    <td style={{ textAlign:'right', fontFamily:'monospace', fontSize:12 }}>{item.unit_cost!=null ? fmtMoney(item.unit_cost) : '—'}</td>
                    <td style={{ textAlign:'right', fontFamily:'monospace', fontSize:12 }}>{item.units_per_purchase!=null ? Number(item.units_per_purchase) : '—'}</td>
                    <td style={{ textAlign:'right', fontFamily:'monospace', fontSize:12 }}>{parFor(par, item.id) ?? '—'}</td>
                    <td>{item.home_loc ? <LocTag code={item.home_loc} /> : <span style={{ color:'#a0aec0' }}>—</span>}</td>
                    <td><Badge color={STATUS_BADGE[st][0]}>{STATUS_BADGE[st][1]}</Badge></td>
                    <td>
                      <button className="btn" style={{ fontSize:11, padding:'3px 10px' }} onClick={() => openEdit(item)}>{st === 'retired' ? 'View' : 'Edit'}</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODULE: CYCLE COUNT (sessions, blind counting)
// Counters never see expected quantities here. The server doesn't send them.
// ─────────────────────────────────────────────────────────────────────────────

// Printable blind recount sheet: SKU and description only, never the system or earlier counts.
function printRecountSheet(session, lines, doorTote) {
  const esc = (v) => String(v == null ? '' : v).replace(/[&<>"]/g, c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;' }[c]));
  const box = '<td class="w"></td>';
  const rows = lines.map(l => `<tr><td class="sku">${esc(l.sku)}</td><td>${esc(l.desc)}</td><td>${l.next_round === 3 ? '3rd count' : 'Recount'}</td>${doorTote ? box + box + box : box}${box}</tr>`).join('');
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Recount sheet ${esc(session.loc)}</title><style>
    body{font-family:Arial,sans-serif;margin:24px;color:#111} h1{font-size:18px;margin:0 0 4px} .sub{font-size:12px;color:#555;margin-bottom:16px}
    table{width:100%;border-collapse:collapse;font-size:13px} th,td{border:1px solid #333;padding:8px 6px;text-align:left;vertical-align:middle}
    th{background:#eee;font-size:11px;text-transform:uppercase} td.w{width:${doorTote ? '70' : '90'}px;height:28px} .sku{font-family:monospace;font-weight:bold;white-space:nowrap}
    .foot{margin-top:24px;font-size:12px} .foot span{display:inline-block;border-bottom:1px solid #333;width:200px;margin:0 24px 0 6px}
    @media print{body{margin:10mm}}</style></head><body>
    <h1>Recount sheet: ${esc(session.loc)}${LOCATIONS[session.loc] ? ' (' + esc(LOCATIONS[session.loc]) + ')' : ''}</h1>
    <div class="sub">Blind recount. Count each item from scratch. Printed ${esc(new Date().toLocaleString())}. ${lines.length} item${lines.length === 1 ? '' : 's'}.</div>
    <table><thead><tr><th>SKU</th><th>Description</th><th>Step</th>${doorTote ? '<th>Door</th><th>Tote</th><th>Total</th>' : '<th>Count</th>'}<th>Initials</th></tr></thead><tbody>${rows}</tbody></table>
    <div class="foot">Counted by<span></span> Date<span></span></div>
    <script>window.onload=function(){window.print()}<\/script></body></html>`;
  const w = window.open('', '_blank');
  if (!w) { window.alert('Allow pop-ups for CPX to print the recount sheet.'); return; }
  w.document.open(); w.document.write(html); w.document.close();
}

const roundLabel = (n) => n === 1 ? 'Count' : n === 2 ? 'Recount' : n === 3 ? '3rd count' : 'Done';
const sessionStatusBadge = (s) => ({
  counting:  ['yellow', 'Counting'],
  submitted: ['blue', 'Waiting for approval'],
  approved:  ['green', 'Approved'],
  cancelled: ['gray', 'Cancelled'],
}[s] || ['gray', s]);

function CountSheet({ sessionId, boot, items, onExit, onToast }) {
  const [sheet, setSheet] = useState(null);
  const [vals, setVals] = useState({}); // entry_id -> { qty } or { door, tote }
  const [counter, setCounter] = useState(String(boot.me.id));
  const [addId, setAddId] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState({}); // entry_id -> true while fixing a counted line

  const load = useCallback(async () => {
    try { setSheet(await ccCall('ims_cc_sheet', { session_id: sessionId })); }
    catch (e) { onToast('Couldn’t load the count: ' + e.message, 'error'); }
  }, [sessionId, onToast]);
  useEffect(() => { load(); }, [load]);

  if (!sheet) return <div style={{ padding:48, textAlign:'center', color:'#a0aec0' }}>Loading count…</div>;
  const { session, doorTote, lines } = sheet;
  const open = session.status === 'counting';
  const todo = lines.filter(l => l.next_round);
  const fixing = lines.filter(l => !l.next_round && editing[l.entry_id]);
  const filled = (l) => {
    const v = vals[l.entry_id] || {};
    return doorTote ? (v.door !== undefined && v.door !== '') || (v.tote !== undefined && v.tote !== '') : (v.qty !== undefined && v.qty !== '');
  };
  const ready = [...todo, ...fixing].filter(filled);
  const setV = (id, patch) => setVals(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  const startEdit = (l) => {
    setEditing(e => ({ ...e, [l.entry_id]: true }));
    setV(l.entry_id, doorTote ? { door: String(l.last_door ?? ''), tote: String(l.last_tote ?? '') } : { qty: String(l.last_qty ?? '') });
  };
  const cancelEdit = (l) => {
    setEditing(e => { const n = { ...e }; delete n[l.entry_id]; return n; });
    setVals(v => { const n = { ...v }; delete n[l.entry_id]; return n; });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = ready.map(l => {
        const v = vals[l.entry_id] || {};
        const edit = !l.next_round;
        return doorTote ? { entry_id: l.entry_id, door_qty: Number(v.door || 0), tote_qty: Number(v.tote || 0), edit } : { entry_id: l.entry_id, qty: Number(v.qty), edit };
      });
      const r = await ccCall('ims_cc_save_counts', { session_id: sessionId, counted_by_id: Number(counter), lines: payload });
      setSheet(r); setVals({}); setEditing({});
      const recounts = r.lines.filter(l => l.next_round && l.next_round > 1).length;
      onToast(`Saved ${r.saved} count${r.saved === 1 ? '' : 's'}${recounts ? ` · ${recounts} need a recount` : ''}`, 'success');
    } catch (e) { onToast('Save failed: ' + e.message, 'error'); }
    finally { setBusy(false); }
  };
  const addLine = async () => {
    if (!addId) return;
    setBusy(true);
    try { setSheet(await ccCall('ims_cc_add_line', { session_id: sessionId, item_id: addId })); setAddId(''); }
    catch (e) { onToast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    const blanks = lines.filter(l => l.next_round === 1).length;
    if (blanks && !window.confirm(`${blanks} line${blanks === 1 ? ' is' : 's are'} still blank. ${blanks === 1 ? 'It' : 'They'}’ll be counted as 0 and taken out of ${session.loc} once the count is approved. Send for approval?`)) return;
    setBusy(true);
    try { await ccCall('ims_cc_submit', { session_id: sessionId, blanks_as_zero: blanks > 0 }); onToast(`${session.loc} count sent for approval${blanks ? ` · ${blanks} blank line${blanks === 1 ? '' : 's'} counted as 0` : ''}`, 'success'); onExit(true); }
    catch (e) { onToast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const onSheet = new Set(lines.map(l => l.item_id));
  const cellInput = { width:80, textAlign:'right', padding:'4px 8px', border:'1px solid #cbd5e0', borderRadius:4, fontSize:13, fontFamily:'monospace' };
  const [sb, sl] = sessionStatusBadge(session.status);

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16, flexWrap:'wrap' }}>
        <button className="btn" style={{ fontSize:12 }} onClick={() => onExit(false)}>← Counts</button>
        <LocTag code={session.loc} />
        <span style={{ fontSize:15, fontWeight:700, color:'#1a202c' }}>{LOCATIONS[session.loc]}</span>
        <Badge color={session.session_type === 'spot' ? 'blue' : 'gray'}>{session.session_type === 'spot' ? 'Spot count' : 'Full count'}</Badge>
        <Badge color={sb}>{sl}</Badge>
        <span style={{ fontSize:12, color:'#718096' }}>{lines.length - todo.length} / {lines.length} done</span>
      </div>

      {open && (
        <div className="card" style={{ padding:'12px 16px', marginBottom:16, display:'flex', gap:16, alignItems:'center', flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <label style={{ ...labelStyle, marginBottom:0 }}>Counted by</label>
            <select className="form-input" value={counter} onChange={e => setCounter(e.target.value)} style={{ fontSize:12, width:200 }}>
              {boot.staff.map(s => <option key={s.id} value={s.id}>{s.name}{s.id === boot.me.id ? ' (me)' : ''}</option>)}
            </select>
          </div>
          <span style={{ fontSize:11, color:'#a0aec0' }}>Entered by {boot.me.name}. Whoever counts or enters can’t approve this session.</span>
          <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
            <button className="btn btn-primary" style={{ fontSize:12 }} onClick={save} disabled={busy || ready.length === 0}>
              {busy ? 'Saving…' : `Save ${ready.length || ''} count${ready.length === 1 ? '' : 's'}`}
            </button>
            <button className="btn" style={{ fontSize:12 }} onClick={submit} disabled={busy || todo.some(l => l.next_round > 1) || fixing.length > 0 || ready.length > 0 || lines.length === 0}
              title={todo.some(l => l.next_round > 1) ? 'Recounts need to be done first' : fixing.length || ready.length ? 'Save your counts first' : ''}>Send for approval</button>
          </div>
        </div>
      )}
      {open && todo.some(l => l.next_round > 1) && (
        <div style={{ padding:'10px 16px', marginBottom:16, borderRadius:8, background:'rgba(214,158,46,0.12)', border:'1px solid rgba(214,158,46,0.35)', fontSize:12, color:'#92600a' }}>
          Some lines need a recount. Count them again from scratch. The earlier count isn’t shown on purpose.
          <button className="btn" style={{ fontSize:11, marginLeft:10, padding:'2px 10px' }}
            onClick={() => printRecountSheet(session, todo.filter(l => l.next_round > 1), doorTote)}>🖨 Print recount sheet</button>
        </div>
      )}

      <div className="card">
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>SKU</th><th>Description</th><th>Step</th>
                {doorTote ? <><th style={{textAlign:'right'}}>Door</th><th style={{textAlign:'right'}}>Tote</th><th style={{textAlign:'right'}}>Total</th></>
                          : <th style={{textAlign:'right'}}>Count</th>}
              </tr>
            </thead>
            <tbody>
              {lines.map(l => {
                const v = vals[l.entry_id] || {};
                const isFixing = open && !l.next_round && !!editing[l.entry_id];
                const need = (open && l.next_round) || isFixing;
                const doneShown = !need && !l.next_round && l.rounds_done > 0;
                return (
                  <tr key={l.entry_id} style={need && l.next_round > 1 ? { background:'rgba(214,158,46,0.07)' } : undefined}>
                    <td style={{ fontFamily:'monospace', fontWeight:700, fontSize:12 }}>{l.sku}</td>
                    <td style={{ color:'#2d3748' }}>{l.desc}</td>
                    <td style={{ whiteSpace:'nowrap' }}>
                      {l.next_round ? <Badge color={l.next_round > 1 ? 'yellow' : 'gray'}>{roundLabel(l.next_round)}</Badge>
                        : isFixing ? <Badge color="blue">Editing</Badge> : <Badge color="green">✓ Counted</Badge>}
                      {open && !l.next_round && l.rounds_done > 0 && (
                        isFixing
                          ? <button className="btn" style={{ fontSize:10, padding:'1px 8px', marginLeft:6 }} onClick={() => cancelEdit(l)}>Cancel</button>
                          : <button className="btn" style={{ fontSize:10, padding:'1px 8px', marginLeft:6 }} onClick={() => startEdit(l)}>Edit</button>
                      )}
                    </td>
                    {doorTote ? (
                      <>
                        <td style={{ textAlign:'right' }}>{need ? <input style={cellInput} inputMode="numeric" value={v.door ?? ''} placeholder="—" onChange={e => setV(l.entry_id, { door: toWhole(e.target.value) })} /> : doneShown ? <span style={{ color:'#718096', fontFamily:'monospace' }}>{l.last_door ?? '—'}</span> : ''}</td>
                        <td style={{ textAlign:'right' }}>{need ? <input style={cellInput} inputMode="numeric" value={v.tote ?? ''} placeholder="—" onChange={e => setV(l.entry_id, { tote: toWhole(e.target.value) })} /> : doneShown ? <span style={{ color:'#718096', fontFamily:'monospace' }}>{l.last_tote ?? '—'}</span> : ''}</td>
                        <td style={{ textAlign:'right', fontFamily:'monospace', fontWeight:700 }}>{need ? (filled(l) ? Number(v.door || 0) + Number(v.tote || 0) : '') : doneShown ? (l.last_qty ?? '') : ''}</td>
                      </>
                    ) : (
                      <td style={{ textAlign:'right' }}>{need ? <input style={cellInput} inputMode="numeric" value={v.qty ?? ''} placeholder="—" onChange={e => setV(l.entry_id, { qty: toWhole(e.target.value) })} /> : doneShown ? <span style={{ fontFamily:'monospace', fontWeight:700 }}>{l.last_qty ?? ''}</span> : ''}</td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="card" style={{ padding:'12px 16px', marginTop:12, display:'flex', gap:8, alignItems:'center', overflow:'visible', position:'relative', zIndex:5 }}>
          <span style={{ fontSize:12, color:'#718096' }}>Found something not on the sheet?</span>
          <div style={{ width:300 }}><SkuCombo items={items.filter(i => !i.retired && !onSheet.has(i.id))} value={addId} onChange={setAddId} placeholder="+ Add item…" /></div>
          <button className="btn" style={{ fontSize:12 }} onClick={addLine} disabled={!addId || busy}>Add</button>
        </div>
      )}
      {doorTote && <div style={{ fontSize:11, color:'#a0aec0', marginTop:8 }}>Count the door shelf and the totes separately. The Total is what gets saved as the item’s quantity at {session.loc}, and it’s the number Daily Deduct takes from.</div>}
      {open && <div style={{ fontSize:11, color:'#a0aec0', marginTop:4 }}>Made a mistake on a counted line? Hit Edit, fix it, and Save. You can do that until the count is sent for approval. Edits show up for the approver. Anything left blank counts as 0 when you send it.</div>}
    </div>
  );
}

function CountModule({ items, inventory, onSave, onToast }) {
  const [boot, setBoot] = useState(null);
  const [active, setActive] = useState(null);
  const [spotLoc, setSpotLoc] = useState('');
  const [spotItems, setSpotItems] = useState([]); // several items can go in one spot count
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setBoot(await ccCall('ims_cc_bootstrap')); }
    catch (e) { onToast('Couldn’t load counts: ' + e.message, 'error'); }
  }, [onToast]);
  useEffect(() => { load(); }, [load]);

  if (!boot) return <div style={{ padding:48, textAlign:'center', color:'#a0aec0' }}>Loading counts…</div>;
  if (active) return <CountSheet sessionId={active} boot={boot} items={items} onToast={onToast} onExit={(changed) => { setActive(null); load(); if (changed) onSave(); }} />;

  const openSessions = boot.sessions.filter(s => ['counting', 'submitted'].includes(s.status));
  const openByLoc = Object.fromEntries(openSessions.map(s => [s.loc, s]));
  const recent = boot.sessions.filter(s => !['counting', 'submitted'].includes(s.status)).slice(0, 15);
  const locsOf = (id) => [...new Set(inventory.filter(r => r.item_id === id && r.qty > 0).map(r => r.loc))].filter(l => boot.countLocs.includes(l));
  const addSpotItem = (id) => {
    if (!id || spotItems.includes(id)) return;
    const next = [...spotItems, id];
    setSpotItems(next);
    // If every picked item is only on hand in one shared spot, fill it in.
    const shared = next.map(locsOf).reduce((a, b) => a.filter(x => b.includes(x)));
    if (!spotLoc && shared.length === 1) setSpotLoc(shared[0]);
  };

  const start = async (loc, type = 'full', itemId = null) => {
    setBusy(true);
    try {
      const r = await ccCall('ims_cc_start', { loc, session_type: type, item_ids: Array.isArray(itemId) ? itemId : itemId ? [itemId] : [] });
      onToast(`${type === 'spot' ? 'Spot count' : 'Count'} started for ${loc}`, 'success');
      setSpotLoc(''); setSpotItems([]);
      setActive(r.session.id);
    } catch (e) { onToast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  const cancel = async (s) => {
    if (!window.confirm(`Cancel the ${s.loc} count? Nothing it counted will be posted.`)) return;
    try { await ccCall('ims_cc_cancel', { session_id: s.id }); onToast(`${s.loc} count cancelled`, 'success'); load(); }
    catch (e) { onToast(e.message, 'error'); }
  };

  return (
    <div>
      <div className="card" style={{ padding:20, marginBottom:20 }}>
        <h3 style={{ fontSize:15, fontWeight:700, marginBottom:4, color:'#1a202c' }}>Start a full count</h3>
        <div style={{ fontSize:12, color:'#718096', marginBottom:14 }}>One session per location. Inventory doesn’t change until an approver approves it. PF1 isn’t counted. P01 should be empty, so a count there catches anything left behind.</div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {boot.countLocs.map(k => {
            const o = openByLoc[k];
            return (
              <button key={k} onClick={() => o ? setActive(o.id) : start(k)} disabled={busy}
                style={{ border:`1px solid ${o ? '#f6ad55' : '#e2e6ea'}`, borderRadius:8, padding:'10px 14px', cursor:'pointer', textAlign:'left', background:'#fff', minWidth:120 }}>
                <div style={{ fontWeight:700, fontSize:13, color:'#e8550a', fontFamily:'monospace' }}>{k}{boot.doorToteLocs.includes(k) && <span style={{ fontSize:9, color:'#a0aec0', marginLeft:6 }}>DOOR+TOTE</span>}</div>
                <div style={{ fontSize:11, color:'#718096', marginTop:2 }}>{LOCATIONS[k]}</div>
                <div style={{ fontSize:10, color: o ? '#92600a' : '#a0aec0', marginTop:2 }}>{o ? 'Count open, tap to resume' : `${inventory.filter(r => r.loc === k && r.qty > 0).length} items on hand`}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="card" style={{ padding:20, marginBottom:20, overflow:'visible', position:'relative', zIndex:5 }}>
        <h3 style={{ fontSize:15, fontWeight:700, marginBottom:4, color:'#1a202c' }}>Spot count</h3>
        <div style={{ fontSize:12, color:'#718096', marginBottom:14 }}>Count a few items in one location, or a whole location, anytime. Pick as many items as you want, then start. You can add more while counting. Same blind count and approval steps.</div>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr auto', gap:12, alignItems:'end', maxWidth:720 }}>
          <div>
            <label style={labelStyle}>Items (leave empty to spot count a whole location)</label>
            <SkuCombo items={items.filter(i => !i.retired && !spotItems.includes(i.id))} value="" onChange={addSpotItem} placeholder="— Type SKU or select to add —" />
          </div>
          <div>
            <label style={labelStyle}>Location</label>
            <select className="form-input" value={spotLoc} onChange={e => setSpotLoc(e.target.value)}>
              <option value="">— Pick —</option>
              {boot.countLocs.map(k => <option key={k} value={k} disabled={!!openByLoc[k]}>{k} — {LOCATIONS[k]}{openByLoc[k] ? ' (count open)' : ''}</option>)}
            </select>
          </div>
          <button className="btn btn-primary" disabled={!spotLoc || busy} onClick={() => start(spotLoc, 'spot', spotItems)}>
            Start spot count{spotItems.length > 1 ? ` (${spotItems.length} items)` : ''}
          </button>
        </div>
        {spotItems.length > 0 && (
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:10, maxWidth:720 }}>
            {spotItems.map(id => {
              const it = items.find(i => i.id === id) || {};
              const locs = locsOf(id);
              return (
                <span key={id} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 8px', borderRadius:6, background:'#f4f6f8', border:'1px solid #e2e6ea', fontSize:12 }}>
                  <strong style={{ fontFamily:'monospace' }}>{it.sku}</strong>
                  <span style={{ color:'#718096' }}>{locs.length ? `on hand at ${locs.join(', ')}` : 'none on hand'}</span>
                  <a style={{ cursor:'pointer', color:'#a0aec0' }} onClick={() => setSpotItems(spotItems.filter(x => x !== id))}>×</a>
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom:20 }}>
        <div style={{ padding:'14px 16px', borderBottom:'1px solid #e2e6ea', fontWeight:700, fontSize:13 }}>Open counts ({openSessions.length})</div>
        {openSessions.length === 0 ? <EmptyState icon="📋" title="No open counts" /> : openSessions.map(s => {
          const [c, l] = sessionStatusBadge(s.status);
          const item = s.item_id ? items.find(i => i.id === s.item_id) : null;
          return (
            <div key={s.id} style={{ padding:'12px 16px', display:'flex', alignItems:'center', gap:12, borderBottom:'1px solid #f0f2f5', flexWrap:'wrap' }}>
              <LocTag code={s.loc} />
              <span style={{ fontSize:12, color:'#4a5568' }}>{s.session_type === 'spot' ? `Spot${item && s.lines === 1 ? ': ' + item.sku : ` (${s.lines} item${s.lines === 1 ? '' : 's'})`}` : 'Full count'}</span>
              <Badge color={c}>{l}</Badge>
              <span style={{ fontSize:11, color:'#718096' }}>{s.lines_done}/{s.lines} counted{s.recounts_pending ? ` · ${s.recounts_pending} recount${s.recounts_pending === 1 ? '' : 's'}` : ''}</span>
              <span style={{ fontSize:11, color:'#a0aec0', marginLeft:'auto' }}>Started {fmtDate(s.created_at)} by {s.created_by_name || '—'}</span>
              {s.status === 'counting' && <button className="btn btn-primary" style={{ fontSize:11 }} onClick={() => setActive(s.id)}>Count</button>}
              <button className="btn" style={{ fontSize:11, color:'#c53030' }} onClick={() => cancel(s)}>Cancel</button>
            </div>
          );
        })}
      </div>

      {recent.length > 0 && (
        <div className="card">
          <div style={{ padding:'14px 16px', borderBottom:'1px solid #e2e6ea', fontWeight:700, fontSize:13 }}>Recent counts</div>
          {recent.map(s => {
            const [c, l] = sessionStatusBadge(s.status);
            return (
              <div key={s.id} style={{ padding:'10px 16px', display:'flex', alignItems:'center', gap:12, borderBottom:'1px solid #f0f2f5' }}>
                <LocTag code={s.loc} /><span style={{ fontSize:12, color:'#4a5568' }}>{s.session_type === 'spot' ? 'Spot' : 'Full'}</span>
                <Badge color={c}>{l}</Badge>
                <span style={{ fontSize:11, color:'#a0aec0', marginLeft:'auto' }}>{s.approved_at ? `Approved ${fmtDate(s.approved_at)} by ${s.approved_by}` : fmtDate(s.created_at)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODULE: REVIEW & APPROVE (expected qty, variances, transfers, mix-ups)
// ─────────────────────────────────────────────────────────────────────────────

const signed = (n) => n == null ? '—' : (n > 0 ? '+' : '') + Number(n).toLocaleString();
const varColor = (n) => n > 0 ? '#276749' : n < 0 ? '#c53030' : '#a0aec0';

function ReviewSession({ sessionId, onExit, onToast }) {
  const [rv, setRv] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setRv(await ccCall('ims_cc_review', { session_id: sessionId })); setErr(null); }
    catch (e) { setErr(e.message); }
  }, [sessionId]);
  useEffect(() => { load(); }, [load]);

  const act = async (q, params, msg) => {
    setBusy(true);
    try { const r = await ccCall(q, params); if (r && r.lines) setRv(r); if (msg) onToast(msg, 'success'); return r; }
    catch (e) { onToast(e.message, 'error'); return null; }
    finally { setBusy(false); }
  };

  if (err) return (
    <div>
      <button className="btn" style={{ fontSize:12, marginBottom:16 }} onClick={() => onExit(false)}>← Sessions</button>
      <div className="card" style={{ padding:20, color:'#c53030', fontSize:13 }}>{err}</div>
    </div>
  );
  if (!rv) return <div style={{ padding:48, textAlign:'center', color:'#a0aec0' }}>Loading review…</div>;
  const s = rv.session;
  const [sb, sl] = sessionStatusBadge(s.status);
  const open = ['counting', 'submitted'].includes(s.status);

  const approve = async () => {
    if (!window.confirm(`Approve the ${s.loc} count and post ${rv.lines.filter(l => l.variance).length} adjustment(s)?`)) return;
    const r = await act('ims_cc_approve', { session_id: sessionId });
    if (r) { onToast(`${s.loc} approved · ${r.posted} adjustment${r.posted === 1 ? '' : 's'} posted`, 'success'); onExit(true); }
  };

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16, flexWrap:'wrap' }}>
        <button className="btn" style={{ fontSize:12 }} onClick={() => onExit(false)}>← Sessions</button>
        <LocTag code={s.loc} /><span style={{ fontSize:15, fontWeight:700 }}>{LOCATIONS[s.loc]}</span>
        <Badge color={s.session_type === 'spot' ? 'blue' : 'gray'}>{s.session_type === 'spot' ? 'Spot count' : 'Full count'}</Badge>
        <Badge color={sb}>{sl}</Badge>
        <span style={{ fontSize:12, color:'#718096' }}>Counted by {s.counted_by || '—'}</span>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:16 }}>
        <StatCard label="Lines" value={rv.lines.length} />
        <StatCard label="Flagged for recount" value={rv.totals.flagged} />
        <StatCard label="Net units" value={signed(rv.totals.units)} />
        <StatCard label="Net $ (before transfers)" value={(rv.totals.dollars < 0 ? '−' : '') + fmtMoney(Math.abs(rv.totals.dollars))} accent />
      </div>

      <div className="card">
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>SKU</th><th>Description</th>
                <th style={{textAlign:'right'}}>Expected</th>
                <th>Counts</th>
                <th style={{textAlign:'right'}}>Variance</th>
                <th style={{textAlign:'right'}}>$</th>
                <th>All locations</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rv.lines.map(l => {
                const hot = l.mixups.length > 0;
                return (
                  <React.Fragment key={l.entry_id}>
                    <tr style={hot ? { background:'rgba(214,158,46,0.10)' } : l.recount_flag ? { background:'rgba(229,62,62,0.04)' } : undefined}>
                      <td style={{ fontFamily:'monospace', fontWeight:700, fontSize:12 }}>{l.sku}</td>
                      <td style={{ color:'#2d3748', maxWidth:240 }}>{l.desc}</td>
                      <td style={{ textAlign:'right', fontFamily:'monospace' }}>{l.system_qty ?? '—'}</td>
                      <td style={{ fontSize:11 }}>
                        {l.rounds.length === 0 ? <span style={{ color:'#a0aec0' }}>Not counted yet</span> : l.rounds.map(r => (
                          <div key={r.round} title={`${roundLabel(r.round)}: counted by ${r.by}, entered by ${r.entered_by || '—'}, ${fmtDate(r.at)}`}>
                            <strong>{r.qty}</strong>{r.door_qty != null && <span style={{ color:'#a0aec0' }}> ({r.door_qty} door + {r.tote_qty} tote)</span>}
                            <span style={{ color:'#a0aec0' }}> · {r.by} · {fmtDate(r.at)}</span>
                            {r.edits > 0 && <span style={{ color:'#92600a' }}> · edited{r.edits > 1 ? ` ${r.edits}×` : ''}, first entered {r.edited_from}</span>}
                          </div>
                        ))}
                        {l.next_round && <Badge color="yellow">{roundLabel(l.next_round)} needed</Badge>}
                      </td>
                      <td style={{ textAlign:'right', fontFamily:'monospace', fontWeight:700, color: varColor(l.variance) }}>
                        {signed(l.variance)}
                        {l.recount_flag && <div style={{ fontSize:10, fontWeight:500, color:'#c53030' }}>⚠ {l.recount_reason}</div>}
                      </td>
                      <td style={{ textAlign:'right', fontFamily:'monospace', fontSize:12, color: varColor(l.dollar_variance) }}>
                        {l.dollar_variance == null ? '—' : (l.dollar_variance < 0 ? '−' : '') + fmtMoney(Math.abs(l.dollar_variance))}
                      </td>
                      <td style={{ fontSize:11 }}>
                        {l.other_locations.length === 0 ? <span style={{ color:'#a0aec0' }}>Only here</span> : (
                          <>
                            <div>Net <strong style={{ color: varColor(l.net_all_locations) }}>{signed(l.net_all_locations)}</strong></div>
                            {l.other_locations.map(o => <div key={o.loc} style={{ color:'#718096' }}>{o.loc} {signed(o.variance)}{o.session_status === 'approved' ? ' (posted)' : ''}</div>)}
                          </>
                        )}
                      </td>
                      <td>
                        {open && l.rounds.length > 0 && l.rounds.length < 3 && !l.next_round && (
                          <button className="btn" style={{ fontSize:11, padding:'3px 8px' }} disabled={busy}
                            onClick={() => act('ims_cc_request_recount', { entry_id: l.entry_id }, `${l.sku} sent for another count`)}>Recount</button>
                        )}
                      </td>
                    </tr>
                    {(l.transfer_candidates.length > 0 || hot) && (
                      <tr style={hot ? { background:'rgba(214,158,46,0.10)' } : undefined}>
                        <td colSpan={8} style={{ fontSize:12, paddingTop:0 }}>
                          {l.transfer_candidates.map(c => {
                            const chosen = l.transfer_decision === 'transfer' && l.transfer_partner === c.entry_id;
                            return (
                              <div key={c.entry_id} style={{ display:'flex', gap:8, alignItems:'center', margin:'4px 0', padding:'8px 10px', borderRadius:6, background:'rgba(232,85,10,0.06)', border:'1px solid rgba(232,85,10,0.2)' }}>
                                <span>↔ {l.loc || s.loc} {signed(l.variance)} and {c.loc} {signed(c.variance)} offset each other. Post {c.transfer_qty} as a <strong>transfer</strong> instead of a loss and a gain?</span>
                                {chosen ? <Badge color="green">Confirmed transfer</Badge> : l.transfer_decision === 'separate' ? <Badge color="gray">Kept as loss/gain</Badge> : <Badge color="yellow">Needs a decision</Badge>}
                                {open && (
                                  <span style={{ marginLeft:'auto', display:'flex', gap:6 }}>
                                    <button className="btn btn-primary" style={{ fontSize:11 }} disabled={busy || chosen}
                                      onClick={() => act('ims_cc_transfer_decide', { entry_id: l.entry_id, partner_entry_id: c.entry_id, decision: 'transfer' }, `${l.sku}: transfer confirmed`)}>Confirm transfer</button>
                                    <button className="btn" style={{ fontSize:11 }} disabled={busy || l.transfer_decision === 'separate'}
                                      onClick={() => act('ims_cc_transfer_decide', { entry_id: l.entry_id, decision: 'separate' }, `${l.sku}: kept as loss/gain`)}>Real loss/gain</button>
                                  </span>
                                )}
                              </div>
                            );
                          })}
                          {hot && (
                            <div style={{ margin:'4px 0', color:'#92600a' }}>
                              ⚠ Possible mix-up: {l.mixups.map(m => `${m.sku} ${signed(m.variance)} at ${m.loc}`).join(', ')}. Check the labels before approving.
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ padding:16, marginTop:16, display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
        {rv.canApprove ? (
          <span style={{ fontSize:12, color:'#276749' }}>Ready to approve. Inventory changes when you approve.</span>
        ) : (
          <span style={{ fontSize:12, color:'#92600a' }}>{rv.blockers.join(' ')}</span>
        )}
        <span style={{ fontSize:11, color:'#a0aec0' }}>Approvers: {rv.approvers.map(a => a.name + (a.primary ? ' (primary)' : '')).join(', ')}</span>
        <button className="btn btn-primary" style={{ marginLeft:'auto' }} disabled={!rv.canApprove || busy} onClick={approve}>Approve &amp; post</button>
      </div>
    </div>
  );
}

function ReviewModule({ onSave, onToast }) {
  const [boot, setBoot] = useState(null);
  const [active, setActive] = useState(null);
  const load = useCallback(async () => {
    try { setBoot(await ccCall('ims_cc_bootstrap')); }
    catch (e) { onToast('Couldn’t load counts: ' + e.message, 'error'); }
  }, [onToast]);
  useEffect(() => { load(); }, [load]);

  if (!boot) return <div style={{ padding:48, textAlign:'center', color:'#a0aec0' }}>Loading…</div>;
  if (!boot.me.isReviewer) {
    return <EmptyState icon="🔒" title="Approvers only" body={`Count review is for ${boot.approvers.map(a => a.name).join(', ') || 'approvers'}. Counts stay blind for everyone else.`} />;
  }
  if (active) return <ReviewSession sessionId={active} onToast={onToast} onExit={(changed) => { setActive(null); load(); if (changed) onSave(); }} />;

  const list = boot.sessions.filter(s => ['counting', 'submitted'].includes(s.status));
  const done = boot.sessions.filter(s => s.status === 'approved').slice(0, 15);
  return (
    <div>
      {boot.approverSource === 'name-match' && (
        <div style={{ padding:'10px 16px', marginBottom:16, borderRadius:8, background:'rgba(232,85,10,0.06)', border:'1px solid rgba(232,85,10,0.2)', fontSize:12, color:'#c44508' }}>
          Approvers were matched by name ({boot.approvers.map(a => a.name).join(', ') || 'none found'}). An admin can confirm them in Owner Summary → Count settings.
        </div>
      )}
      {!boot.me.isApprover && <div style={{ fontSize:12, color:'#718096', marginBottom:12 }}>You can review as an admin, but only approvers can approve.</div>}
      <div className="card" style={{ marginBottom:20 }}>
        <div style={{ padding:'14px 16px', borderBottom:'1px solid #e2e6ea', fontWeight:700, fontSize:13 }}>Counts to review ({list.length})</div>
        {list.length === 0 ? <EmptyState icon="✅" title="Nothing to review" /> : list.map(s => {
          const [c, l] = sessionStatusBadge(s.status);
          return (
            <div key={s.id} style={{ padding:'12px 16px', display:'flex', alignItems:'center', gap:12, borderBottom:'1px solid #f0f2f5', flexWrap:'wrap' }}>
              <LocTag code={s.loc} />
              <span style={{ fontSize:12, color:'#4a5568' }}>{s.session_type === 'spot' ? 'Spot count' : 'Full count'}</span>
              <Badge color={c}>{l}</Badge>
              <span style={{ fontSize:11, color:'#718096' }}>{s.lines_done}/{s.lines} counted{s.recounts_pending ? ` · ${s.recounts_pending} recount${s.recounts_pending === 1 ? '' : 's'} pending` : ''}</span>
              <span style={{ marginLeft:'auto' }} />
              {s.i_counted
                ? <span style={{ fontSize:11, color:'#a0aec0' }}>🔒 You counted or entered on this one</span>
                : <button className="btn btn-primary" style={{ fontSize:11 }} onClick={() => setActive(s.id)}>Review</button>}
            </div>
          );
        })}
      </div>
      {done.length > 0 && (
        <div className="card">
          <div style={{ padding:'14px 16px', borderBottom:'1px solid #e2e6ea', fontWeight:700, fontSize:13 }}>Recently approved</div>
          {done.map(s => (
            <div key={s.id} style={{ padding:'10px 16px', display:'flex', alignItems:'center', gap:12, borderBottom:'1px solid #f0f2f5' }}>
              <LocTag code={s.loc} /><span style={{ fontSize:12 }}>{s.session_type === 'spot' ? 'Spot' : 'Full'}</span>
              <span style={{ fontSize:11, color:'#a0aec0', marginLeft:'auto' }}>Approved {fmtDate(s.approved_at)} by {s.approved_by}</span>
              {!s.i_counted && <button className="btn" style={{ fontSize:11 }} onClick={() => setActive(s.id)}>View</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODULE: OWNER SUMMARY (monthly recap + count settings)
// ─────────────────────────────────────────────────────────────────────────────

// Admin-only: dollar recount threshold, approvers, which locations count Door + Tote.
function CountSettings({ onToast }) {
  const [boot, setBoot] = useState(null);
  const [threshold, setThreshold] = useState('');
  const [approvers, setApprovers] = useState([]);
  const [dt, setDt] = useState([]);
  const [addId, setAddId] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    ccCall('ims_cc_bootstrap').then(b => {
      setBoot(b); setThreshold(String(b.threshold)); setApprovers(b.approvers.map(a => a.id)); setDt(b.doorToteLocs);
    }).catch(e => onToast('Couldn’t load count settings: ' + e.message, 'error'));
  }, [onToast]);
  if (!boot) return <div style={{ padding:16, fontSize:12, color:'#a0aec0' }}>Loading…</div>;
  const name = (id) => (boot.staff.find(s => s.id === id) || {}).name || `#${id}`;
  const move = (i, d) => setApprovers(a => { const n = [...a]; const j = i + d; if (j < 0 || j >= n.length) return a; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const save = async () => {
    setSaving(true);
    try {
      await ccCall('ims_cc_settings_save', { threshold: Number(threshold), approver_ids: approvers, door_tote_locs: dt });
      onToast('Count settings saved', 'success');
    } catch (e) { onToast('Save failed: ' + e.message, 'error'); }
    finally { setSaving(false); }
  };
  return (
    <div style={{ padding:20, display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:24 }}>
      <div>
        <label style={labelStyle}>Recount if off by more than ($)</label>
        <input className="form-input" type="number" min="0" step="1" value={threshold} onChange={e => setThreshold(e.target.value)} style={{ width:140 }} />
        <div style={{ fontSize:11, color:'#a0aec0', marginTop:4 }}>Lines are also flagged when off by more than 3 units. Whichever comes first. New counts use the new amount.</div>
      </div>
      <div>
        <label style={labelStyle}>Approvers (first one is primary)</label>
        {approvers.map((id, i) => (
          <div key={id} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, marginBottom:4 }}>
            <span style={{ flex:1 }}>{name(id)}{i === 0 && <Badge color="blue">Primary</Badge>}</span>
            <button className="btn" style={{ fontSize:10, padding:'1px 6px' }} onClick={() => move(i, -1)} disabled={i === 0}>↑</button>
            <button className="btn" style={{ fontSize:10, padding:'1px 6px' }} onClick={() => move(i, 1)} disabled={i === approvers.length - 1}>↓</button>
            <button className="btn" style={{ fontSize:10, padding:'1px 6px', color:'#c53030' }} onClick={() => setApprovers(a => a.filter(x => x !== id))}>✕</button>
          </div>
        ))}
        <div style={{ display:'flex', gap:6, marginTop:6 }}>
          <select className="form-input" value={addId} onChange={e => setAddId(e.target.value)} style={{ fontSize:12 }}>
            <option value="">+ Add approver…</option>
            {boot.staff.filter(s => !approvers.includes(s.id)).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button className="btn" style={{ fontSize:11 }} disabled={!addId} onClick={() => { setApprovers(a => [...a, Number(addId)]); setAddId(''); }}>Add</button>
        </div>
        <div style={{ fontSize:11, color:'#a0aec0', marginTop:4 }}>Nobody can approve a count they counted or entered.</div>
      </div>
      <div>
        <label style={labelStyle}>Count Door + Tote separately at</label>
        <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
          {boot.countLocs.map(l => (
            <label key={l} style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, fontFamily:'monospace', padding:'3px 8px', border:'1px solid #e2e6ea', borderRadius:6, cursor:'pointer' }}>
              <input type="checkbox" checked={dt.includes(l)} onChange={e => setDt(d => e.target.checked ? [...d, l] : d.filter(x => x !== l))} />{l}
            </label>
          ))}
        </div>
      </div>
      <div style={{ gridColumn:'1 / -1' }}>
        <button className="btn btn-primary" style={{ fontSize:12 }} onClick={save} disabled={saving || threshold === '' || approvers.length === 0}>{saving ? 'Saving…' : 'Save count settings'}</button>
      </div>
    </div>
  );
}

const thisMonth = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }).slice(0, 7);
const monthName = (m) => new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
const prevMonth = (m) => { const y = Number(m.slice(0, 4)), mo = Number(m.slice(5, 7)); return mo === 1 ? `${y - 1}-12` : `${y}-${String(mo - 1).padStart(2, '0')}`; };
const parseMoney = (s) => Number(String(s || '0').replace(/[$,]/g, '')) || 0;
// Latest snapshot saved for a month. Old snapshots have random ids, so match on the month name.
const snapshotFor = (snapshots, m) => [...snapshots].filter(s => s.month === monthName(m))
  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0] || null;

// ── Phase 5: Monthly Recap ──────────────────────────────────────────────────
// Count variance (units and $), largest variances by item, inventory value and
// change from last month, documented losses (Damage / Waste / Quality Test), and
// shrink as a % of sales. Shrink = count loss + documented losses, over the month's
// sales from the CPX admin Sales Report.
function OwnerSummary({ items, inventory, ownerSettings, snapshots, isAdmin, onSave, onToast }) {
  const [month, setMonth] = useState(thisMonth());
  const [recap, setRecap] = useState(null);
  const [sales, setSales] = useState(null); // number, or { error }
  const [showSettings, setShowSettings] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    setRecap(null); setSales(null);
    ccCall('ims_cc_recap', { month }).then(r => { if (live) setRecap(r); }).catch(e => { if (live) setRecap({ error: e.message }); });
    // Same source as the Sales Report page, so the numbers always match it.
    fetchSalesReport(`${month.slice(5, 7)}/01/${month.slice(0, 4)}`)
      .then(d => { if (live) setSales(parseMoney(d?.summary?.revenue)); })
      .catch(e => { if (live) setSales({ error: e.message }); });
    return () => { live = false; };
  }, [month]);

  const itemMap = Object.fromEntries(items.map(i => [i.id, i]));
  const liveValue = inventory.reduce((s, r) => s + (r.qty ?? 0) * Number(itemMap[r.item_id]?.unit_cost ?? 0), 0);
  const isCurrent = month === thisMonth();
  const snapThis = snapshotFor(snapshots, month);
  const snapPrev = snapshotFor(snapshots, prevMonth(month));
  // Current month: what's on hand right now. Past month: the value saved when that month was closed.
  const monthValue = isCurrent ? liveValue : (snapThis ? Number(snapThis.total_value) : null);
  const prevValue = snapPrev ? Number(snapPrev.total_value) : null;
  const momDiff = monthValue != null && prevValue != null ? monthValue - prevValue : null;
  const momPct = momDiff != null && prevValue ? (momDiff / prevValue) * 100 : null;

  const ok = recap && !recap.error;
  const salesNum = typeof sales === 'number' ? sales : null;
  const countLoss = ok ? Math.max(0, -recap.dollars) : 0;       // net count shortfall in $
  const documented = ok ? Number(recap.documented || 0) : 0;
  const shrinkDollars = countLoss + documented;
  const pct = (n) => salesNum ? (n / salesNum) * 100 : null;
  const shrinkPct = ok ? pct(shrinkDollars) : null;
  const money = (n) => (n < 0 ? '−' : '') + fmtMoney(Math.abs(n));
  const signedMoney = (n) => (n > 0 ? '+' : n < 0 ? '−' : '') + fmtMoney(Math.abs(n));

  const byFamily = {};
  inventory.forEach(r => {
    const item = itemMap[r.item_id];
    if (!item) return;
    const fam = item.family || 'Other';
    if (!byFamily[fam]) byFamily[fam] = { qty:0, value:0 };
    byFamily[fam].qty += r.qty ?? 0;
    byFamily[fam].value += (r.qty ?? 0) * Number(item.unit_cost ?? 0);
  });

  const finalizeMonth = async () => {
    if (!ok || salesNum == null) return;
    const msg = snapThis
      ? `Replace the ${monthName(month)} snapshot? It will use today’s on-hand value (${fmtMoney(liveValue)}).`
      : `Close out ${monthName(month)}? This saves today’s on-hand value (${fmtMoney(liveValue)}) as the month-end value. Do it on the last day of the month or first thing on the 1st.`;
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      // gross_profit column holds monthly SALES now (shrink is % of sales).
      // One snapshot per month: a fixed id means saving again replaces it.
      await saveSnapshot({ id: `m-${month}`, month: monthName(month), total_value: liveValue,
        total_loss: shrinkDollars, total_gain: Number(recap.gainDollars || 0),
        shrink_pct: shrinkPct ?? 0, gross_profit: salesNum, created_at: dateStr() });
      await saveOwnerSettings({ ...ownerSettings, month: monthName(month), prev_total_value: liveValue });
      onToast('Month snapshot saved', 'success'); onSave();
    } catch (e) { onToast('Snapshot failed: ' + e.message, 'error'); }
    finally { setSaving(false); }
  };

  const head = (t, sub) => (
    <div style={{ padding:'14px 16px', borderBottom:'1px solid #e2e6ea', display:'flex', alignItems:'baseline', gap:8 }}>
      <span style={{ fontWeight:700, fontSize:13, color:'#1a202c' }}>{t}</span>
      {sub && <span style={{ fontSize:11, color:'#a0aec0' }}>{sub}</span>}
    </div>
  );
  const canSnap = isCurrent || month === prevMonth(thisMonth());

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16, flexWrap:'wrap' }}>
        <h3 style={{ fontSize:15, fontWeight:700, margin:0 }}>Monthly Recap</h3>
        <input className="form-input" type="month" value={month} max={thisMonth()} onChange={e => e.target.value && setMonth(e.target.value)} style={{ width:170, fontSize:12 }} />
        {snapThis && <Badge color="green">Closed {fmtDate(snapThis.created_at)}</Badge>}
        <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
          {isAdmin && <button className="btn" style={{ fontSize:12 }} onClick={() => setShowSettings(v => !v)}>⚙ Count settings</button>}
          {canSnap && (
            <button className="btn btn-primary" style={{ fontSize:12 }} onClick={finalizeMonth} disabled={saving || !ok || salesNum == null}>
              {snapThis ? 'Re-save month snapshot' : 'Save month snapshot'}
            </button>
          )}
        </div>
      </div>

      {showSettings && isAdmin && (
        <div className="card" style={{ marginBottom:20 }}>
          <div style={{ padding:'14px 16px', fontWeight:700, fontSize:13, borderBottom:'1px solid #e2e6ea' }}>Count settings (admins only)</div>
          <CountSettings onToast={onToast} />
        </div>
      )}

      {recap && recap.error && <div className="card" style={{ padding:12, marginBottom:16, color:'#c53030', fontSize:12 }}>Recap failed: {recap.error}</div>}
      {sales && sales.error && <div className="card" style={{ padding:12, marginBottom:16, color:'#c53030', fontSize:12 }}>Couldn’t load sales: {sales.error}</div>}

      {/* Variance + value */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:12, marginBottom:12 }}>
        <StatCard label="Unit variance" value={ok ? signed(recap.units) : '…'}
          sub={ok ? `${recap.sessions} approved count${recap.sessions === 1 ? '' : 's'}${recap.transfers ? ` · ${recap.transfers} transfer units left out` : ''}` : ''} />
        <StatCard label="Dollar variance" value={ok ? money(recap.dollars) : '…'}
          sub={ok ? `${fmtMoney(Math.abs(recap.lossDollars || 0))} short · ${fmtMoney(recap.gainDollars || 0)} over` : ''} />
        <StatCard label={isCurrent ? 'Total inventory value (now)' : `Inventory value, end of ${monthName(month).split(' ')[0]}`}
          value={monthValue != null ? fmtMoney(monthValue) : 'No snapshot'}
          sub={!isCurrent && monthValue == null ? 'This month was never closed out' : ''} />
        <StatCard label={`Change from ${monthName(prevMonth(month))}`}
          value={momDiff != null ? signedMoney(momDiff) : '—'}
          sub={momDiff != null ? (momPct != null ? `${momPct >= 0 ? '+' : ''}${momPct.toFixed(1)}% · was ${fmtMoney(prevValue)}` : `was ${fmtMoney(prevValue)}`) : `No snapshot saved for ${monthName(prevMonth(month))}`} />
      </div>

      {/* Losses + shrink */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:12, marginBottom:20 }}>
        <StatCard label="Documented losses" value={ok ? fmtMoney(documented) : '…'} sub="Damage + Waste + Quality Test" />
        <StatCard label="Sales (CPX sales report)" value={salesNum != null ? fmtMoney(salesNum) : sales && sales.error ? 'Unavailable' : '…'} />
        <StatCard label="Total shrink" value={ok ? fmtMoney(shrinkDollars) : '…'} sub="Count shortfall + documented losses" />
        <StatCard label="Shrink % of sales" accent value={shrinkPct != null ? shrinkPct.toFixed(2) + '%' : '…'}
          sub={shrinkPct != null ? `Counts ${pct(countLoss).toFixed(2)}% · Documented ${pct(documented).toFixed(2)}%` : salesNum === 0 ? 'No sales this month' : ''} />
      </div>

      {ok && (
        <div style={{ display:'grid', gridTemplateColumns:'minmax(0,3fr) minmax(0,2fr)', gap:16, marginBottom:20 }}>
          <div className="card">
            {head('Largest variances', 'by item, all locations and counts this month')}
            {recap.largest.length === 0 ? <EmptyState icon="📊" title="No variances this month" /> : (
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th>SKU</th><th>Description</th><th>Locations</th><th style={{textAlign:'right'}}>Units</th><th style={{textAlign:'right'}}>$</th></tr></thead>
                  <tbody>{recap.largest.map(l => (
                    <tr key={l.sku}>
                      <td style={{ fontFamily:'monospace', fontWeight:700, fontSize:12 }}>{l.sku}</td>
                      <td style={{ fontSize:12 }}>{l.desc}</td>
                      <td><div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>{(l.locs || []).map(c => <LocTag key={c} code={c} />)}</div></td>
                      <td style={{ textAlign:'right', color: varColor(l.units) }}>{signed(l.units)}</td>
                      <td style={{ textAlign:'right', fontFamily:'monospace', color: varColor(l.dollars) }}>{money(l.dollars)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </div>
          <div className="card">
            {head('Documented losses', 'by reason')}
            <table>
              <thead><tr><th>Reason</th><th style={{textAlign:'right'}}>Units</th><th style={{textAlign:'right'}}>$</th><th style={{textAlign:'right'}}>% of sales</th></tr></thead>
              <tbody>
                {recap.losses.map(l => (
                  <tr key={l.reason}>
                    <td>
                      <div style={{ fontWeight:600 }}>{l.reason}</div>
                      {l.reason === 'Waste' && (l.wasteLogDollars > 0 || l.inventoryDollars > 0) && (
                        <div style={{ fontSize:10, color:'#a0aec0' }}>Inventory {fmtMoney(l.inventoryDollars)} · Waste Log {fmtMoney(l.wasteLogDollars)}</div>
                      )}
                    </td>
                    <td style={{ textAlign:'right', color: l.units ? '#2d3748' : '#a0aec0' }}>{l.units ? l.units.toLocaleString() : '—'}</td>
                    <td style={{ textAlign:'right', fontFamily:'monospace', color: l.dollars ? '#c53030' : '#a0aec0' }}>{fmtMoney(l.dollars)}</td>
                    <td style={{ textAlign:'right', color:'#718096' }}>{salesNum ? pct(l.dollars).toFixed(2) + '%' : '—'}</td>
                  </tr>
                ))}
                <tr style={{ borderTop:'2px solid #e2e6ea' }}>
                  <td style={{ fontWeight:700 }}>Total</td><td />
                  <td style={{ textAlign:'right', fontFamily:'monospace', fontWeight:700 }}>{fmtMoney(documented)}</td>
                  <td style={{ textAlign:'right', fontWeight:700 }}>{salesNum ? pct(documented).toFixed(2) + '%' : '—'}</td>
                </tr>
              </tbody>
            </table>
            <div style={{ padding:'8px 16px', fontSize:11, color:'#a0aec0', lineHeight:1.5 }}>
              Waste Log units aren’t counted here since they’re typed in, not SKUs.
              {recap.wasteLog && recap.wasteLog.damagedSkipped > 0 && <> {fmtMoney(recap.wasteLog.damagedSkipped)} of “Damaged” Waste Log entries left out so damage isn’t counted twice.</>}
              {recap.wasteLog && !recap.wasteLog.available && <> Waste Log couldn’t be read.</>}
            </div>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom:20 }}>
        <div style={{ padding:'14px 16px', fontWeight:700, fontSize:13, color:'#1a202c', borderBottom:'1px solid #e2e6ea' }}>Inventory by Family</div>
        <div className="tbl-wrap">
          <table>
            <thead><tr><th>Family</th><th style={{textAlign:'right'}}>Units</th><th style={{textAlign:'right'}}>Value</th><th style={{textAlign:'right'}}>% of Total</th></tr></thead>
            <tbody>
              {Object.entries(byFamily).filter(([,{value}])=>value>0).sort((a,b)=>b[1].value-a[1].value).map(([fam,{qty,value}]) => (
                <tr key={fam}>
                  <td style={{ fontWeight:600, color:'#2d3748' }}>{fam}</td>
                  <td style={{ textAlign:'right', color:'#718096' }}>{qty.toLocaleString()}</td>
                  <td style={{ textAlign:'right', fontFamily:'monospace', color:'#1a202c', fontWeight:600 }}>{fmtMoney(value)}</td>
                  <td style={{ textAlign:'right', color:'#718096' }}>{liveValue>0?((value/liveValue)*100).toFixed(1):0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {snapshots.length > 0 && (
        <div className="card">
          <div style={{ padding:'14px 16px', fontWeight:700, fontSize:13, color:'#1a202c', borderBottom:'1px solid #e2e6ea' }}>Monthly history</div>
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>Month</th><th style={{textAlign:'right'}}>Inventory Value</th><th style={{textAlign:'right'}}>Shrink $</th><th style={{textAlign:'right'}}>Count overage</th><th style={{textAlign:'right'}}>Sales*</th><th style={{textAlign:'right'}}>Shrink %</th></tr></thead>
              <tbody>
                {[...snapshots].sort((a,b)=>new Date(b.created_at)-new Date(a.created_at)).map(s=>(
                  <tr key={s.id}>
                    <td style={{ fontWeight:600, color:'#2d3748' }}>{s.month}</td>
                    <td style={{ textAlign:'right', fontFamily:'monospace' }}>{fmtMoney(s.total_value)}</td>
                    <td style={{ textAlign:'right', color:'#c53030', fontFamily:'monospace' }}>{fmtMoney(s.total_loss)}</td>
                    <td style={{ textAlign:'right', color:'#276749', fontFamily:'monospace' }}>{fmtMoney(s.total_gain)}</td>
                    <td style={{ textAlign:'right', fontFamily:'monospace' }}>{fmtMoney(s.gross_profit)}</td>
                    <td style={{ textAlign:'right', color:'#718096' }}>{Number(s.shrink_pct ?? 0).toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding:'8px 16px', fontSize:11, color:'#a0aec0' }}>*Snapshots saved before the count rebuild show gross profit in this column. Shrink $ before Phase 5 is count loss only.</div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODULE: AUDIT LOG
// ─────────────────────────────────────────────────────────────────────────────

function AuditLog({ items }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search:'', action:'', loc:'', dateFrom:'', dateTo:'' });
  const itemMap = Object.fromEntries(items.map(i => [i.id, i]));

  useEffect(() => {
    setLoading(true);
    loadAuditLog({ action:filters.action||null, loc:filters.loc||null, dateFrom:filters.dateFrom||null, dateTo:filters.dateTo||null })
      .then(rows => setLogs(rows)).catch(console.error).finally(() => setLoading(false));
  }, [filters.dateFrom, filters.dateTo, filters.action, filters.loc]);

  const filtered = logs.filter(entry => {
    if (!filters.search) return true;
    const q = filters.search.toLowerCase();
    const item = itemMap[entry.item_id];
    return (item?.sku??'').toLowerCase().includes(q) || (item?.desc??'').toLowerCase().includes(q) || (entry.notes??'').toLowerCase().includes(q);
  });

  const actionColors = { 'Put':'green','Move':'blue','Count Finalized':'gray','Recount Approved':'green','Sale':'red','Item Removed via ✕':'yellow','Damage':'red','Damage Hold':'yellow','Retired':'gray','Count Adjustment':'yellow','Count Transfer':'blue','Count Approved':'green','Count Reclassified':'blue','Location Cleared':'gray','Production In':'gray','Production Out':'green','Unmatched Lines':'yellow' };

  return (
    <div>
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ padding:'12px 16px', display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
          <input className="form-input" type="text" placeholder="Search SKU, notes…" value={filters.search} onChange={e=>setFilters(f=>({...f,search:e.target.value}))} style={{ fontSize:12, width:200 }} />
          <select className="form-input" value={filters.action} onChange={e=>setFilters(f=>({...f,action:e.target.value}))} style={{ fontSize:12, width:160 }}>
            <option value="">All Actions</option>
            {['Put','Move','Sale','Production In','Production Out','Damage','Damage Hold','Count Adjustment','Count Transfer','Count Approved','Count Reclassified','Location Cleared','Unmatched Lines','Retired','Count Finalized','Recount Approved','Item Removed via ✕'].map(a=><option key={a} value={a}>{a}</option>)}
          </select>
          <select className="form-input" value={filters.loc} onChange={e=>setFilters(f=>({...f,loc:e.target.value}))} style={{ fontSize:12, width:160 }}>
            <option value="">All Locations</option>
            {Object.entries(LOCATIONS).map(([k,v])=><option key={k} value={k}>{k} — {v}</option>)}
          </select>
          <input className="form-input" type="date" value={filters.dateFrom} onChange={e=>setFilters(f=>({...f,dateFrom:e.target.value}))} style={{ fontSize:12, width:140 }} />
          <input className="form-input" type="date" value={filters.dateTo} onChange={e=>setFilters(f=>({...f,dateTo:e.target.value}))} style={{ fontSize:12, width:140 }} />
          {(filters.action||filters.loc||filters.dateFrom||filters.dateTo) && (
            <button className="btn" style={{ fontSize:11 }} onClick={()=>setFilters({search:'',action:'',loc:'',dateFrom:'',dateTo:''})}>Clear</button>
          )}
          <span style={{ fontSize:11, color:'#a0aec0', marginLeft:'auto' }}>{filtered.length} entries</span>
        </div>
      </div>
      {loading ? <div style={{ padding:48, textAlign:'center', color:'#a0aec0' }}>Loading…</div> : (
        <div className="card">
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>Time</th><th>Module</th><th>Action</th><th>SKU</th><th>Location</th><th style={{textAlign:'right'}}>Qty</th><th>Reason</th><th>Notes</th></tr></thead>
              <tbody>
                {filtered.map((entry,i) => {
                  const item = itemMap[entry.item_id];
                  return (
                    <tr key={entry.id??i}>
                      <td style={{ fontSize:11, color:'#a0aec0', whiteSpace:'nowrap' }}>{fmtDate(entry.created_at)}</td>
                      <td style={{ fontSize:11, color:'#718096' }}>{entry.module}</td>
                      <td><Badge color={actionColors[entry.action]||'gray'}>{entry.action}</Badge></td>
                      <td style={{ fontSize:12, color:'#1a202c' }}>
                        <span style={{ fontFamily:'monospace', fontWeight:700 }}>{item?.sku??entry.item_id}</span>
                        {item?.desc && <div style={{ fontSize:11, color:'#718096', maxWidth:220 }}>{item.desc}</div>}
                      </td>
                      <td>{entry.loc&&<LocTag code={entry.loc}/>}</td>
                      <td style={{ textAlign:'right', fontFamily:'monospace', fontWeight:700, color:'#1a202c' }}>{entry.qty!=null?entry.qty.toLocaleString():'—'}</td>
                      <td style={{ fontSize:11, color:'#718096' }}>{entry.reason_code ? putReasonLabel(entry.reason_code) : '—'}</td>
                      <td style={{ fontSize:11, color:'#718096', maxWidth:240 }}>{entry.notes||'—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filtered.length===0 && <EmptyState icon="📋" title="No log entries" body="Actions will appear here as inventory is managed." />}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// CPX NAME → SKU MAPPING  (110 confirmed entries from CPX_Name_Mapping_FINAL)
// ─────────────────────────────────────────────────────────────────────────────

const CPX_NAME_MAP = {
  "1/2 dozen - whale soft crabs, cleaned/frozen": "CP001",
  "1 dozen - whale soft crabs, cleaned/frozen": "CP001",
  "1/2 dozen - jumbo soft crabs, cleaned/frozen": "CP002",
  "1/2 dozen - prime soft crabs, cleaned/frozen": "CP003",
  "fresh - 1 lb jumbo lump maryland crabmeat": "CP005",
  "fresh - 1 lb backfin lump maryland crabmeat": "CP006",
  "pasteurized - 1 lb jumbo lump maryland crabmeat": "CP007",
  "pasteurized - 1 lb backfin lump maryland crabmeat": "CP008",
  "pasteurized - 1 lb maryland claw crabmeat": "CP010",
  "pasteurized - 1 lb maryland gourmet lump crabmeat": "CP488",
  "fresh - 1 lb maryland gourmet lump crabmeat": "CP488F",
  "pasteurized - 12oz maryland claw cocktail crabmeat": "CP110",
  "1 lb pack jonah crab - claw cocktail": "CP490",
  "1 lb jonah crab - triple scored claws": "CP491",
  "1 lb - jonah crab - crab meat": "CP492",
  "1 dozen - 2 oz. lump maryland crab cakes": "CP018",
  "1/2 dozen - 2 oz. lump maryland crab cakes": "CP018",
  "1/2 dozen - 4 oz. lump maryland crab cakes": "CP016",
  "1 dozen - 4 oz. lump maryland crab cakes": "CP016",
  "1/2 dozen - 4 oz. all-jumbo lump maryland crab cakes": "CP017",
  "1/2 dozen - 6 oz. lump maryland crab cakes": "CP015",
  "1/2 dozen - 6 oz. all-jumbo lump maryland crab cakes": "CP458",
  "frozen, 1 pound big-momma key west pink shrimp": "CP0101",
  "frozen, 1 pounds mega monster key west pink shrimp": "CP600",
  "frozen, 1 pound big momma gulf white shrimp": "CP028",
  "frozen, 1 pound jumbo key west pink shrimp": "CP0100",
  "frozen, 1 pounds jumbo gulf white shrimp": "CP0102",
  "x lbs. canadian opilio large snow crab legs": "CP033",
  "1 lb. canadian opilio large": "CP033",
  "3 lbs. canadian opilio large": "CP033",
  "6 lbs. canadian opilio large": "CP033",
  "9 lbs. canadian opilio large": "CP033",
  "x lbs. canadian opilio jumbo snow crab legs": "CP034",
  "3 lbs. canadian opilio jumbo": "CP034",
  "6 lbs. canadian opilio jumbo": "CP034",
  "9 lbs. canadian opilio jumbo": "CP034",
  "x lbs. canadian opilio colossal snow crab legs": "CP035",
  "3 lbs. canadian opilio colossal": "CP035",
  "6 lbs. canadian opilio colossal": "CP035",
  "9 lbs. canadian opilio colossal": "CP035",
  "x lbs. bairdi alaskan large snow crab legs": "CP450",
  "1 lbs. bairdi alaskan large": "CP450",
  "3 lbs. bairdi alaskan large": "CP450",
  "6 lbs. bairdi alaskan large": "CP450",
  "9 lbs. bairdi alaskan large": "CP450",
  "x lbs. bairdi alaskan jumbo snow crab legs": "CP451",
  "1 lbs. bairdi alaskan jumbo": "CP451",
  "3 lbs. bairdi alaskan jumbo": "CP451",
  "6 lbs. bairdi alaskan jumbo": "CP451",
  "9 lbs. bairdi alaskan jumbo": "CP451",
  "x lbs. bairdi alaskan colossal snow crab legs": "CP452",
  "1 lbs. bairdi alaskan colossal": "CP452",
  "3 lbs. bairdi alaskan colossal": "CP452",
  "6 lbs. bairdi alaskan colossal": "CP452",
  "9 lbs. bairdi alaskan colossal": "CP452",
  "12 oz package canadian opilio snow crab meat": "CP453",
  "1 lb. pack canadian opilio snow crab claw cocktail": "CP454",
  "1 pound colossal king crab": "CP031",
  "5 lbs. colossal king crab legs": "CP031",
  "10 lbs. colossal king crab legs": "CP031",
  "2-pack, 6-7 oz. lobster tail": "CP025",
  "2-pack, 7-8 oz. lobster tail": "CP024",
  "2-pack, 8-10 oz. lobster tail": "CP023",
  "2-pack, 12-14 oz. lobster tail": "CP022",
  "2-pack, 16-20 oz. lobster tail": "CP021",
  "1 lbs, 6 to 9 claws per pound, lobster claws": "CP463",
  "1 lbs iqf colossal sea scallops (under 10 ct./lb.)": "CP039",
  "1 lbs iqf colossal sea scallops": "CP039",
  "1 lbs iqf jumbo sea scallops (10-20 ct/lb)": "CP040",
  "1 lbs iqf jumbo sea scallops": "CP040",
  "1 lbs. iqf jumbo sea scallops": "CP040",
  "100 count middle neck clams": "CP036",
  "1 dozen middle neck clams": "CP036",
  "50 count middle neck clams": "CP036",
  "1 pints shucked oysters": "CP038",
  "1 gallon shucked oysters": "CP500",
  "red snapper, 1 pack contains (4) 5-6oz individually frozen servings.": "CP493",
  "strip bass (rockfish), 1 pack contains (4) 5-6oz individually frozen servings.": "CP494",
  "salmon fillet, 5 lbs.": "CP062",
  "crabplace.com spicy crab seasoning (8 oz.)": "CP115",
  "crabplace.com spicy crab seasoning": "CP115",
  "old bay seasoning (16 oz.)": "070328820468",
  "old bay seasoning": "070328820468",
  "cindy's kitchen fresh cocktail sauce (8oz)": "814453027152",
  "cindy's kitchen fresh cocktail sauce": "814453027152",
  "cindy's kitchen fresh tartar sauce (8oz)": "814453027213",
  "cindy's kitchen fresh tartar sauce": "814453027213",
  "epicurean butter - garlic parmesan butter": "CP473",
  "epicurean butter - lemon garlic herb butter": "CP474",
  "j.o. brand seasoning (16 oz.)": "014477909162",
  "j.o. brand seasoning": "014477909162",
  "j.o. crab cake mix (makes 2 lbs.)": "014477090358",
  "j.o. crab cake mix": "014477090358",
  "j.o. soft crab batter (10 oz.)": "014477034109",
  "j.o. soft crab batter": "014477034109",
  "crab mallet (ea.)": "CP042",
  "crab mallet": "CP042",
  "4 crab mallets & 8 oz. crab seasoning": "CP0800",
  "seafood cracker": "CP051",
  "lobster shell cracker": "CP051",
  "crab picking knife": "719469213028",
  "oyster knife (ea.)": "719469213080",
  "oyster knife": "719469213080",
  "cases - gel packs": "CP200",
  "lbs - dry ice": "CP4000",
  // Packing Material box lines deduct as the completed unit that ships (Matt, 2026-10-12).
  "cp11": "CP481",
  "cp18": "CP482",
  "cp30": "CP483",
  "cp34": "CP484",
  "cp42": "CP485",

  // Additional variants discovered from live product report
  "1 lbs. colossal snow crab legs": "CP035",
  "pound colossal snow crab": "CP035",
  "pound jumbo snow crab": "CP034",
  "pound large snow crab": "CP033",
  "1 lbs. large snow crab legs": "CP033",
  "1 lbs. jumbo snow crab legs": "CP034",
  "fresh - 1 lb. jumbo lump maryland crabmeat": "CP005",
  "fresh - 1 lb. backfin lump maryland crabmeat": "CP006",
  "fresh - 1 lb. maryland gourmet lump crabmeat": "CP488F",
  "pasteurized - 1 lb. jumbo lump maryland crabmeat": "CP007",
  "pasteurized - 1 lb. backfin lump maryland crabmeat": "CP008",
  "pasteurized - 1 lb. maryland claw crabmeat": "CP010",
  "pasteurized - 1 lb. maryland gourmet lump crabmeat": "CP488",
  "fresh - (1) 1 lb. maryland gourmet lump crabmeat": "CP488F",
  "fresh - (1) 1 lb maryland gourmet lump crabmeat": "CP488F",
  "1 pound big-momma key west pink shrimp - iqf arrives frozen": "CP0101",
  "1 pound big momma key west pink shrimp": "CP0101",
  "pound jumbo gulf white shrimp, frozen": "CP0102",
  "pound jumbo gulf white shrimp": "CP0102",
  "pound big momma gulf white shrimp": "CP028",
  "pound mega monster key west pink shrimp": "CP600",
  "1 dozen - jumbo soft crabs, cleaned/frozen": "CP002",
  "1 dozen - prime soft crabs, cleaned/frozen": "CP003",
  "8-10 oz. lobster tail (single)": "CP023",
  "16-20 oz. lobster tail (single)": "CP021",
  "12-14 oz. lobster tail (single)": "CP022",
  "7-8 oz. lobster tail (single)": "CP024",
  "6-7 oz. lobster tail (single)": "CP025",
  "14-16 oz. lobster tail": "CP029",
  "16-20 oz. lobster tail": "CP021",
  "pint shucked oysters": "CP038",
  "100 count fresh oysters": "CP036",

  // Additional variants from live report 2026-04-22 and 2026-04-30
  "1 dozen - 6 oz. lump maryland crab cakes": "CP015",
  "pound jumbo snow crab": "CP034",
  "pound large snow crab": "CP033",
  "pound colossal snow crab": "CP035",
  "2 lbs cooked crab claws": "CP460", // blue crab claws, not CP454 snow crab cocktail (Carman, Oct 2026)
  "2 lbs. cooked crab claws": "CP460",
  "fresh - (1) 1 lb. maryland gourmet lump crabmeat": "CP488F",
  "fresh - (1) 1 lb maryland gourmet lump crabmeat": "CP488F",
  "pasteurized - (1) 1 lb. maryland gourmet lump crabmeat": "CP488",
  "pasteurized - (1) 1 lb maryland gourmet lump crabmeat": "CP488",
  "pasteurized - 1 lb. backfin lump maryland crabmeat": "CP008",
  "pasteurized - 1 lb. jumbo lump maryland crabmeat": "CP007",
  "fresh - 1 lb. backfin lump maryland crabmeat": "CP006",
  "fresh - 1 lb. jumbo lump maryland crabmeat": "CP005",
  "6-7 oz. lobster tail (single)": "CP025",
  "7-8 oz. lobster tail (single)": "CP024",
  "8-10 oz. lobster tail (single)": "CP023",
  "12-14 oz. lobster tail (single)": "CP022",
  "16-20 oz. lobster tail (single)": "CP021",
  "pound iqf jumbo sea scallops (10-20 ct/lb)": "CP040",
  "pound iqf colossal sea scallops (under 10 ct/lb)": "CP039",
  "pound iqf colossal sea scallops": "CP039",
  "1 pound jumbo key west pink shrimp - iqf arrives frozen": "CP0100",
  "1 pound big-momma key west pink shrimp - iqf arrives frozen": "CP0101",
  "1 pound mega-monster key west pink shrimp - iqf arrives frozen": "CP600",
  "pound jumbo gulf white shrimp, frozen": "CP0102",
  "1 dozen - prime soft crabs, cleaned/frozen": "CP003",
};


// ─────────────────────────────────────────────────────────────────────────────
// PAR LEVELS  (quarterly — from Quater_Par_Levels.xlsx)
// ─────────────────────────────────────────────────────────────────────────────

// Par levels now live in the database (ims_par_levels) and are edited per SKU in Items.

const PAR_QS = ['q1', 'q2', 'q3', 'q4'];

function getCurrentQuarter() {
  const month = new Date().getMonth() + 1; // 1-12
  if (month <= 3)  return 'q1';
  if (month <= 6)  return 'q2';
  if (month <= 9)  return 'q3';
  return 'q4';
}

// Current quarter's par for one item, or null when it has none.
function parFor(par, itemId) {
  const p = par && par[itemId];
  if (!p) return null;
  const v = p[getCurrentQuarter()];
  return v == null ? null : Number(v);
}


// ─────────────────────────────────────────────────────────────────────────────
// MODULE: DAILY DEDUCT (Phase 4)
// Reads the CPX product list, previews what comes out of each home location,
// and posts only after someone clicks Apply. Never pulls from overflow.
// ─────────────────────────────────────────────────────────────────────────────

// Names are matched EXACTLY after tidying (case, periods, commas, "lbs" vs "lb").
// No partial matching: it used to send gift cards ("None") to Whale Soft Crabs.
const normName = (s) => String(s || '').replace(/<[^>]*>/g, ' ').replace(/&amp;/g, '&').toLowerCase()
  .replace(/[.,]/g, ' ').replace(/\blbs\b/g, 'lb').replace(/\s+/g, ' ').trim();

// Pinned names the old partial matcher used to catch, plus fixes.
const DD_EXTRA_NAMES = {
  '1 lb bairdi alaskan colossal snow crab legs': 'CP452',
  '1 lb bairdi alaskan jumbo snow crab legs': 'CP451',
  '1 lb bairdi alaskan large snow crab legs': 'CP450',
  'pound colossal king crab': 'CP031',
  'pound big momma gulf white shrimp frozen': 'CP028',
  'gallon shucked oysters': 'CP500',
  '1 dozen - 4 oz all-jumbo lump maryland crab cakes': 'CP017',
  '1 dozen - 6 oz all-jumbo lump maryland crab cakes': 'CP458',
  '1 lb - jonah crab - triple scored claws': 'CP491',
  '1lb pack - jonah crab - claw cocktail (frozen)': 'CP490',
  '100 count fresh oysters': 'CP037', // was CP036 (Middlenecks, the clams)
};
// Lines that are known and intentionally not deducted.
const DD_SKIP_NAMES = {
  'table covering': 'Table coverings aren’t tracked',
  'crabplace com vip club membership': 'VIP membership isn’t a physical product',
  'none': 'Gift card, not a product',
  'bushel male hard crabs': 'Hard crabs are off (reconciled monthly)',
  'bushel female hard crabs': 'Hard crabs are off (reconciled monthly)',
};
// Packing Material lines ship as completed units, deducted from R03.
const DD_BOX_UNITS = { CP11: 'CP481', CP18: 'CP482', CP30: 'CP483', CP34: 'CP484', CP42: 'CP485' };
// Sold by the dozen on the list, counted in half dozens in inventory.
const DD_HALF_DOZEN = new Set(['CP015', 'CP016', 'CP017', 'CP458', 'CP001', 'CP002', 'CP003']);
// Gel packs ship one at a time but move in inventory by the case.
const DD_GEL_SKU = 'CP200';
const DD_GEL_PER_CASE = 36;

let DD_NAME_INDEX = null;
const ddNameIndex = () => {
  if (!DD_NAME_INDEX) {
    DD_NAME_INDEX = {};
    Object.entries(CPX_NAME_MAP).forEach(([k, v]) => { DD_NAME_INDEX[normName(k)] = v; });
    Object.entries(DD_EXTRA_NAMES).forEach(([k, v]) => { DD_NAME_INDEX[normName(k)] = v; });
  }
  return DD_NAME_INDEX;
};

// Product-list quantity → inventory units for one line.
// Live lobster 2-packs and the 24-28 oz tail 2-pack. The product list sells these one at a time.
const DD_TWO_PACK_EACH = new Set(['CP516', 'CP520', 'CP521', 'CP522', 'CP523', 'CP524']);

function ddConvert(name, qty, item) {
  const n = normName(name);
  let out = qty, note = null;
  if ((item.sku === 'CP036' || item.sku === 'CP037') && /100 count/.test(n)) {
    // One unit is a dozen (a bag of 13). 100 count = 8, 50 count = 4. The list reports in 100-counts. Round up.
    out = Math.ceil(qty * 8 - 1e-9);
    note = `${Math.round(qty * 100)} count → ${out} unit${out === 1 ? '' : 's'} (dozens)`;
  } else if (DD_TWO_PACK_EACH.has(item.sku) && /\(ea\b|\bea\b|\beach\b|single/.test(n)) {
    // The list counts each lobster or tail; these SKUs are 2-packs, so 2 = 1 unit (Carman, Nov 2026).
    out = qty / 2; note = `${qty} each → ${out} unit${out === 1 ? '' : 's'} (2-packs)`;
  } else if (/lobster tail/.test(n) && /single/.test(n)) {
    // The list counts single tails; inventory moves them as units of 2 tails (Carman, Oct 2026).
    out = qty / 2; note = `${qty} single tails → ${out} unit${out === 1 ? '' : 's'} of 2`;
  } else if (DD_HALF_DOZEN.has(item.sku) && /^1 dozen/.test(n)) {
    out = qty * 2; note = `${qty} dozen → ${out} half dozens`;
  } else if (item.family === 'Snow Crab') {
    const m = n.match(/(\d+(?:\.\d+)?)\s*lb\s*pack/);
    if (m && Number(m[1]) > 1) { out = qty * Number(m[1]); note = `${qty} × ${m[1]} lb packs → ${out} lb`; }
  }
  if (!Number.isInteger(out)) { const r = Math.round(out); note = (note ? note + '; ' : '') + `rounded ${out} → ${r}`; out = r; }
  return { qty: out, note };
}

function DeductSettings({ items, boot, onChanged, onToast }) {
  const [q, setQ] = useState('');
  const [homes, setHomes] = useState({});
  const [busy, setBusy] = useState(null);
  const rows = boot.settings.filter(s => !q || s.sku.toLowerCase().includes(q.toLowerCase()) || (s.desc || '').toLowerCase().includes(q.toLowerCase()));
  const toggle = async (s) => {
    setBusy(s.item_id);
    try { await ddCall('ims_dd_toggle', { item_id: s.item_id, enabled: !s.deduct_enabled }); onToast(`${s.sku} daily deduct ${s.deduct_enabled ? 'off' : 'on'}`, 'success'); onChanged(); }
    catch (e) { onToast(e.message, 'error'); } finally { setBusy(null); }
  };
  const saveHome = async (s) => {
    setBusy(s.item_id);
    try { await ddCall('ims_dd_set_home', { item_id: s.item_id, loc: homes[s.item_id] }); onToast(`${s.sku} home set to ${normLoc(homes[s.item_id])}`, 'success'); onChanged(); }
    catch (e) { onToast(e.message, 'error'); } finally { setBusy(null); }
  };
  return (
    <div className="card">
      <div style={{ padding:'12px 16px', borderBottom:'1px solid #e2e6ea', display:'flex', gap:12, alignItems:'center' }}>
        <strong style={{ fontSize:13 }}>Deduct settings per SKU</strong>
        <input className="form-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search SKU…" style={{ fontSize:12, width:220 }} />
        <span style={{ fontSize:11, color:'#a0aec0' }}>Hard crabs and dry ice start switched off.</span>
      </div>
      <div className="tbl-wrap" style={{ maxHeight:480, overflowY:'auto' }}>
        <table>
          <thead><tr><th>SKU</th><th>Description</th><th>Daily deduct</th><th>Home location</th></tr></thead>
          <tbody>
            {rows.map(s => (
              <tr key={s.item_id}>
                <td style={{ fontFamily:'monospace', fontWeight:700, fontSize:12 }}>{s.sku}</td>
                <td style={{ fontSize:12 }}>{s.desc}</td>
                <td>
                  <button className={`btn ${s.deduct_enabled ? 'btn-primary' : ''}`} style={{ fontSize:11, padding:'3px 10px', minWidth:52 }} disabled={busy === s.item_id} onClick={() => toggle(s)}>
                    {s.deduct_enabled ? 'On' : 'Off'}
                  </button>
                  {!s.explicit && s.default_off && <span style={{ fontSize:10, color:'#a0aec0', marginLeft:6 }}>off by default</span>}
                </td>
                <td>
                  <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                    {DD_BOX_UNITS_SKUS.has(s.sku) ? <span style={{ fontSize:11, color:'#718096' }}>Always R03</span> : (
                      <>
                        <input className="form-input" style={{ width:80, fontFamily:'monospace', fontSize:12 }} value={homes[s.item_id] ?? (s.home_loc || '')}
                          onChange={e => setHomes(h => ({ ...h, [s.item_id]: normLoc(e.target.value) }))} placeholder="—" />
                        {homes[s.item_id] !== undefined && homes[s.item_id] !== (s.home_loc || '') && LOCATIONS[normLoc(homes[s.item_id])] && (
                          <button className="btn" style={{ fontSize:11 }} disabled={busy === s.item_id} onClick={() => saveHome(s)}>Save</button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
const DD_BOX_UNITS_SKUS = new Set(Object.values(DD_BOX_UNITS));

function DailyDeduct({ items, inventory, onSave, onToast }) {
  const todayIso = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const [date, setDate] = useState(todayIso);
  const [boot, setBoot] = useState(null);
  const [view, setView] = useState('run'); // run | settings
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [ack, setAck] = useState(false);
  const [result, setResult] = useState(null);
  const [mapPick, setMapPick] = useState({});
  const [homePick, setHomePick] = useState({});

  const loadBoot = useCallback(async () => {
    try { setBoot(await ddCall('ims_dd_bootstrap')); } catch (e) { onToast('Couldn’t load Daily Deduct: ' + e.message, 'error'); }
  }, [onToast]);
  useEffect(() => { loadBoot(); }, [loadBoot]);

  const bySku = Object.fromEntries(items.map(i => [i.sku, i]));
  const byId = Object.fromEntries(items.map(i => [i.id, i]));
  const enabled = boot ? Object.fromEntries(boot.settings.map(s => [s.item_id, s.deduct_enabled])) : {};
  const ranDates = boot ? Object.fromEntries(boot.runs.map(r => [r.ship_date, r])) : {};

  const buildPreview = useCallback(async () => {
    if (!boot) return;
    setLoading(true); setResult(null); setAck(false);
    try {
      const [itemRows, packingRows] = await Promise.all([
        neonQuery('product_report_items', { startDate: date, endDate: date }),
        neonQuery('product_report_packing', { startDate: date, endDate: date }),
      ]);
      const dbMap = Object.fromEntries(boot.nameMap.map(m => [m.name_key, m]));
      const idx = ddNameIndex();
      const lines = {}; // item_id -> line
      const skipped = [], unmatched = [];
      const addLine = (item, qty, name, note) => {
        const l = lines[item.id] || (lines[item.id] = { item, qty: 0, names: [], notes: [] });
        l.qty += qty; l.names.push(name); if (note) l.notes.push(note);
      };
      itemRows.forEach(r => {
        const raw = String(r.item_name || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        const qty = Number(r.total_qty || 0);
        if (!(qty > 0)) return;
        const key = normName(raw);
        const db = dbMap[key];
        if (db && !db.item_id) { skipped.push({ name: raw || '(blank)', qty, reason: db.skip_reason }); return; }
        const sku = db ? (byId[db.item_id] || {}).sku : idx[key];
        const item = sku ? bySku[sku] : null;
        if (!item) {
          if (DD_SKIP_NAMES[key]) { skipped.push({ name: raw || '(blank)', qty, reason: DD_SKIP_NAMES[key] }); return; }
          unmatched.push({ name: raw || '(blank name)', key, qty, category: r.category });
          return;
        }
        const c = ddConvert(raw, qty, item);
        addLine(item, c.qty, raw, c.note);
      });
      let gel = 0, dry = 0;
      packingRows.forEach(r => {
        const n = Number(r.order_count || 0);
        gel += Number(r.total_gel_packs || 0); dry += Number(r.total_dry_ice || 0);
        if (!n) return;
        if (r.box_type === 'CP10') { skipped.push({ name: 'CP10 box', qty: n, reason: 'CP10 is no longer carried' }); return; }
        const unitSku = DD_BOX_UNITS[r.box_type];
        const item = unitSku && bySku[unitSku];
        if (!item) { unmatched.push({ name: r.box_type ? `Box ${r.box_type}` : 'Orders with no box type', key: normName(`box ${r.box_type || 'none'}`), qty: n, category: 'Packing Material', noMap: !r.box_type }); return; }
        addLine(item, n, `${r.box_type} box`, `${r.box_type} → ${unitSku} completed unit`);
      });
      if (dry > 0) skipped.push({ name: 'Dry ice', qty: dry, reason: 'Dry ice isn’t tracked' });
      if (gel > 0) {
        // The list counts single gel packs; CP200 is tracked in cases of 36.
        const gelItem = bySku[DD_GEL_SKU];
        const cases = Math.round(gel / DD_GEL_PER_CASE);
        if (!gelItem) skipped.push({ name: 'Gel packs', qty: gel, reason: `${DD_GEL_SKU} isn’t set up as an item`, warn: true });
        else if (cases > 0) addLine(gelItem, cases, 'Gel packs', `${gel} packs ÷ ${DD_GEL_PER_CASE} → ${cases} case${cases === 1 ? '' : 's'} (exact ${(gel / DD_GEL_PER_CASE).toFixed(2)})`);
        else skipped.push({ name: 'Gel packs', qty: gel, reason: `Under half a case (${DD_GEL_PER_CASE} per case), nothing deducted` });
      }

      const ready = [], off = [], noHome = [];
      const homeOf = Object.fromEntries(boot.settings.map(s => [s.item_id, s.home_loc]));
      Object.values(lines).forEach(l => {
        const loc = DD_BOX_UNITS_SKUS.has(l.item.sku) ? 'R03' : (homeOf[l.item.id] || '');
        const have = loc ? (inventory.find(r => r.item_id === l.item.id && r.loc === loc)?.qty ?? 0) : null;
        const overflow = inventory.filter(r => r.item_id === l.item.id && r.loc !== loc && r.qty > 0);
        const row = { ...l, loc, have, after: have != null ? have - l.qty : null, overflow };
        if (enabled[l.item.id] === false) off.push(row);
        else if (!loc) noHome.push(row);
        else ready.push(row);
      });
      const sort = (a, b) => a.item.sku.localeCompare(b.item.sku);
      setPreview({ date, ready: ready.sort(sort), off: off.sort(sort), noHome: noHome.sort(sort), skipped, unmatched });
    } catch (e) { onToast('Couldn’t load the product list: ' + e.message, 'error'); }
    finally { setLoading(false); }
  }, [boot, date, items, inventory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-run the preview after a setting changes so it stays honest.
  const refresh = async () => { await loadBoot(); };
  useEffect(() => { if (preview && boot) buildPreview(); }, [boot]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async () => {
    setApplying(true);
    try {
      const r = await ddCall('ims_dd_apply', {
        ship_date: preview.date,
        lines: preview.ready.map(l => ({ item_id: l.item.id, sku: l.item.sku, qty: l.qty, names: l.names, note: l.notes.join('; ') || null })),
        unmatched: preview.unmatched.map(u => ({ name: u.name, qty: u.qty })),
        unmatched_ack: ack,
      });
      setResult({ ...r, date: preview.date }); setPreview(null);
      onToast(`Deducted ${r.posted} SKU${r.posted === 1 ? '' : 's'} for ${preview.date}`, 'success');
      onSave();
    } catch (e) { onToast(e.message, 'error'); }
    finally { setApplying(false); }
  };
  const mapName = async (u, itemId, skip) => {
    try {
      await ddCall('ims_dd_map_name', itemId ? { name_key: u.key, item_id: itemId } : { name_key: u.key, skip_reason: skip });
      onToast(itemId ? `“${u.name}” now deducts ${(byId[itemId] || {}).sku}` : `“${u.name}” marked as not deducted`, 'success');
      await refresh();
    } catch (e) { onToast(e.message, 'error'); }
  };
  const setHome = async (l) => {
    try { await ddCall('ims_dd_set_home', { item_id: l.item.id, loc: homePick[l.item.id] }); onToast(`${l.item.sku} home set`, 'success'); await refresh(); }
    catch (e) { onToast(e.message, 'error'); }
  };
  const toggleOn = async (l) => {
    try { await ddCall('ims_dd_toggle', { item_id: l.item.id, enabled: true }); onToast(`${l.item.sku} daily deduct on`, 'success'); await refresh(); }
    catch (e) { onToast(e.message, 'error'); }
  };

  if (!boot) return <div style={{ padding:48, textAlign:'center', color:'#a0aec0' }}>Loading Daily Deduct…</div>;
  const already = ranDates[date];

  const negativesPanel = boot.negatives.length > 0 && (
    <div className="card" style={{ padding:'12px 16px', marginBottom:16, borderLeft:'4px solid #fc8181' }}>
      <div style={{ fontWeight:700, fontSize:13, color:'#c53030', marginBottom:6 }}>Home locations below zero ({boot.negatives.length})</div>
      {boot.negatives.map(n => (
        <div key={n.item_id + n.loc} style={{ fontSize:12, color:'#4a5568', marginBottom:2 }}>
          <strong>{n.sku}</strong>: {n.loc} is at {n.qty}.{' '}
          {n.overflow && n.overflow.length ? `A restock move from ${n.overflow.map(o => `${o.loc} (${o.qty})`).join(' or ')} may be missing.` : 'Nothing in overflow either. Worth a spot count.'}
        </div>
      ))}
      <div style={{ fontSize:11, color:'#a0aec0', marginTop:4 }}>These clear on their own once the move is entered.</div>
    </div>
  );

  const head = (
    <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16, flexWrap:'wrap' }}>
      <button className={`btn ${view === 'run' ? 'btn-primary' : ''}`} style={{ fontSize:12 }} onClick={() => setView('run')}>Run deduct</button>
      <button className={`btn ${view === 'settings' ? 'btn-primary' : ''}`} style={{ fontSize:12 }} onClick={() => setView('settings')}>SKU settings</button>
    </div>
  );
  if (view === 'settings') return <div>{head}<DeductSettings items={items} boot={boot} onChanged={refresh} onToast={onToast} /></div>;

  if (!preview) {
    return (
      <div>
        {head}{negativesPanel}
        {result && (
          <div className="card" style={{ padding:16, marginBottom:16, borderLeft:'4px solid #68d391' }}>
            <strong>{result.date} deducted:</strong> {result.posted} SKUs, {result.units} units.
            {result.negatives.length > 0 && <span style={{ color:'#c53030' }}> {result.negatives.length} home location{result.negatives.length === 1 ? '' : 's'} went below zero (see above).</span>}
          </div>
        )}
        <div className="card" style={{ padding:24, maxWidth:520, marginBottom:16 }}>
          <h3 style={{ fontSize:15, fontWeight:700, marginBottom:8 }}>Daily Deduct</h3>
          <p style={{ fontSize:13, color:'#718096', marginBottom:16, lineHeight:1.5 }}>
            Pulls the day’s CPX product list and shows what comes out of each SKU’s home location. Nothing changes until you click Apply.
          </p>
          <label style={labelStyle}>Ship date</label>
          <input className="form-input" type="date" value={date} max={todayIso} onChange={e => setDate(e.target.value)} style={{ width:200, marginBottom:12 }} />
          {already && <div style={{ fontSize:12, color:'#92600a', marginBottom:12 }}>Already applied by {already.applied_by} on {fmtDate(already.applied_at)}.</div>}
          <div><button className="btn btn-primary" onClick={buildPreview} disabled={loading}>{loading ? 'Loading…' : 'Preview'}</button></div>
        </div>
        {boot.runs.length > 0 && (
          <div className="card">
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #e2e6ea', fontWeight:700, fontSize:13 }}>Recent runs</div>
            {boot.runs.slice(0, 10).map(r => (
              <div key={r.ship_date} style={{ padding:'8px 16px', borderBottom:'1px solid #f0f2f5', fontSize:12, display:'flex', gap:12 }}>
                <strong>{r.ship_date}</strong><span>{r.line_count} SKUs</span>
                {r.unmatched_count > 0 && <Badge color="yellow">{r.unmatched_count} unmatched</Badge>}
                {r.negative_count > 0 && <Badge color="red">{r.negative_count} went negative</Badge>}
                <span style={{ marginLeft:'auto', color:'#a0aec0' }}>{r.applied_by} · {fmtDate(r.applied_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const pv = preview;
  const negCount = pv.ready.filter(l => l.after < 0).length;
  const blocked = pv.noHome.length > 0 || !!ranDates[pv.date] || (pv.unmatched.length > 0 && !ack) || pv.ready.length === 0;
  const section = (title, children, color) => (
    <div className="card" style={{ marginBottom:16, borderLeft: color ? `4px solid ${color}` : undefined }}>
      <div style={{ padding:'12px 16px', borderBottom:'1px solid #e2e6ea', fontWeight:700, fontSize:13 }}>{title}</div>
      {children}
    </div>
  );

  return (
    <div>
      {head}{negativesPanel}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16, flexWrap:'wrap' }}>
        <div>
          <h3 style={{ fontSize:15, fontWeight:700, margin:0 }}>Preview for {pv.date}</h3>
          <div style={{ fontSize:12, color:'#718096' }}>
            {pv.ready.length} to deduct{negCount ? ` · ${negCount} will go below zero` : ''}{pv.noHome.length ? ` · ${pv.noHome.length} need a home` : ''} · {pv.off.length} switched off · {pv.unmatched.length} unmatched
          </div>
        </div>
        <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
          <button className="btn" style={{ fontSize:12 }} onClick={() => setPreview(null)}>← Back</button>
          <button className="btn btn-primary" style={{ fontSize:12 }} onClick={apply} disabled={blocked || applying}>{applying ? 'Applying…' : `Apply (${pv.ready.length} SKUs)`}</button>
        </div>
      </div>
      {ranDates[pv.date] && <div className="card" style={{ padding:12, marginBottom:16, color:'#92600a', fontSize:12 }}>{pv.date} was already applied by {ranDates[pv.date].applied_by}. It can’t be applied twice.</div>}

      {pv.unmatched.length > 0 && section(`Unmatched names (${pv.unmatched.length}): pick the SKU or mark it not deducted`, (
        <div>
          {pv.unmatched.map((u, i) => (
            <div key={i} style={{ padding:'8px 16px', borderBottom:'1px solid #f0f2f5', display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
              <span style={{ fontSize:12, flex:'1 1 280px' }}><strong>{u.name}</strong> <span style={{ color:'#a0aec0' }}>· {u.category} · {u.qty}</span></span>
              {!u.noMap && (
                <>
                  <div style={{ width:260 }}><SkuCombo items={items.filter(x => !x.retired)} value={mapPick[u.key] || ''} onChange={id => setMapPick(m => ({ ...m, [u.key]: id }))} placeholder="Pick SKU…" /></div>
                  <button className="btn btn-primary" style={{ fontSize:11 }} disabled={!mapPick[u.key]} onClick={() => mapName(u, mapPick[u.key])}>Save match</button>
                  <button className="btn" style={{ fontSize:11 }} onClick={() => { const why = window.prompt('Why isn’t this deducted? (e.g. not a physical product)'); if (why) mapName(u, null, why); }}>Not deducted</button>
                </>
              )}
            </div>
          ))}
          <label style={{ display:'flex', gap:8, alignItems:'center', padding:'10px 16px', fontSize:12, color:'#92600a' }}>
            <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} />
            I’ve seen these. Apply the rest anyway (they’re logged with the run).
          </label>
        </div>
      ), '#f6ad55')}

      {pv.noHome.length > 0 && section(`Needs a home location (${pv.noHome.length})`, pv.noHome.map(l => (
        <div key={l.item.id} style={{ padding:'8px 16px', borderBottom:'1px solid #f0f2f5', display:'flex', gap:10, alignItems:'center' }}>
          <span style={{ fontFamily:'monospace', fontWeight:700, fontSize:12 }}>{l.item.sku}</span><span style={{ fontSize:12 }}>{l.item.desc} · sold {l.qty}</span>
          <span style={{ fontSize:11, color:'#718096' }}>on hand at {l.overflow.map(o => `${o.loc} (${o.qty})`).join(', ') || 'nowhere'}</span>
          <input className="form-input" style={{ width:80, marginLeft:'auto', fontFamily:'monospace' }} value={homePick[l.item.id] || ''} onChange={e => setHomePick(h => ({ ...h, [l.item.id]: normLoc(e.target.value) }))} placeholder="R03" />
          <button className="btn btn-primary" style={{ fontSize:11 }} disabled={!LOCATIONS[normLoc(homePick[l.item.id])]} onClick={() => setHome(l)}>Set home</button>
        </div>
      )), '#fc8181')}

      {section(`Will be deducted (${pv.ready.length})`, (
        <div className="tbl-wrap">
          <table>
            <thead><tr><th>SKU</th><th>Description</th><th style={{textAlign:'right'}}>Deduct</th><th>From</th><th style={{textAlign:'right'}}>Now → after</th><th>Notes</th></tr></thead>
            <tbody>
              {pv.ready.map(l => (
                <tr key={l.item.id} style={l.after < 0 ? { background:'rgba(229,62,62,0.05)' } : undefined}>
                  <td style={{ fontFamily:'monospace', fontWeight:700, fontSize:12 }}>{l.item.sku}</td>
                  <td style={{ fontSize:12 }}>{l.item.desc}<div style={{ fontSize:10, color:'#a0aec0' }}>{l.names.join(' + ')}</div></td>
                  <td style={{ textAlign:'right', fontWeight:700 }}>{l.qty}</td>
                  <td><LocTag code={l.loc} /></td>
                  <td style={{ textAlign:'right', fontFamily:'monospace', color: l.after < 0 ? '#c53030' : '#4a5568' }}>{l.have} → {l.after}</td>
                  <td style={{ fontSize:11, color:'#718096' }}>
                    {l.notes.join('; ')}
                    {l.after < 0 && <div style={{ color:'#c53030' }}>⚠ {l.loc} goes negative. {l.overflow.length ? `A move from ${l.overflow.map(o => o.loc).join(' or ')} may be missing.` : 'Nothing in overflow.'} It’s recorded anyway.</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {pv.off.length > 0 && section(`Switched off, not deducted (${pv.off.length})`, pv.off.map(l => (
        <div key={l.item.id} style={{ padding:'8px 16px', borderBottom:'1px solid #f0f2f5', display:'flex', gap:10, alignItems:'center', fontSize:12 }}>
          <span style={{ fontFamily:'monospace', fontWeight:700 }}>{l.item.sku}</span><span>{l.item.desc} · {l.qty}</span>
          <button className="btn" style={{ fontSize:11, marginLeft:'auto' }} onClick={() => toggleOn(l)}>Turn on</button>
        </div>
      )))}

      {pv.skipped.length > 0 && section(`Known lines not deducted (${pv.skipped.length})`, pv.skipped.map((s, i) => (
        <div key={i} style={{ padding:'6px 16px', borderBottom:'1px solid #f0f2f5', fontSize:12, color: s.warn ? '#92600a' : '#718096' }}>
          {s.warn ? '⚠ ' : ''}{s.name} · {s.qty} · {s.reason}
        </div>
      )))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODULE: PRODUCTION BATCHES (Phase 4)
// One screen: inputs come out, outputs go in, one step. No P01 moves.
// ─────────────────────────────────────────────────────────────────────────────

const BATCH_TYPES = [
  { key: 'standard', label: 'Standard processing', help: 'Repacks and crab cakes. Expect 1 lb out per 1 lb in; crab cakes use the 36 lb batch yields.' },
  { key: 'grading', label: 'Grading', help: 'One input, several graded sizes out (snow crab, hard crabs). Values use item master costs.' },
  { key: 'box_assembly', label: 'Box assembly', help: 'Outer box + cooler → completed unit into R03. Always 1 to 1.' },
  { key: 'fish', label: 'Fish processing', help: 'Fish in by the pound, 4-packs out. Expected = pounds ÷ 1.5, rounded down.' },
];
const blankLine = () => ({ key: uid(), item_id: '', loc: '', qty: '' });

function BatchLines({ title, lines, setLines, items, inventory, isInput, single }) {
  const upd = (k, patch) => setLines(ls => ls.map(l => l.key === k ? { ...l, ...patch } : l));
  return (
    <div style={{ flex:1, minWidth:320 }}>
      <div style={{ fontWeight:700, fontSize:13, marginBottom:8 }}>{title}</div>
      {lines.map(l => {
        const it = items.find(i => i.id === l.item_id);
        const stock = inventory.filter(r => r.item_id === l.item_id && r.qty > 0);
        const have = stock.find(r => r.loc === l.loc)?.qty ?? 0;
        return (
          <div key={l.key} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 80px 24px', gap:6, marginBottom:6, alignItems:'start' }}>
            <SkuCombo items={items.filter(i => !i.retired)} value={l.item_id} onChange={id => {
              const item = items.find(i => i.id === id);
              const locs = inventory.filter(r => r.item_id === id && r.qty > 0);
              upd(l.key, { item_id: id, loc: isInput ? (locs.length === 1 ? locs[0].loc : (item?.home_loc && locs.some(r => r.loc === item.home_loc) ? item.home_loc : '')) : (item?.home_loc || '') });
            }} placeholder="Item…" />
            {isInput ? (
              <select className="form-input" value={l.loc} onChange={e => upd(l.key, { loc: e.target.value })} style={{ fontSize:12 }}>
                <option value="">From…</option>
                {stock.map(r => <option key={r.loc} value={r.loc}>{r.loc} ({r.qty})</option>)}
              </select>
            ) : (
              <input className="form-input" value={l.loc} onChange={e => upd(l.key, { loc: normLoc(e.target.value) })} placeholder="To…" style={{ fontFamily:'monospace', fontSize:12 }} />
            )}
            <QtyInput value={l.qty} onChange={v => upd(l.key, { qty: v })} />
            {!single && lines.length > 1 ? <button className="btn" style={{ fontSize:11, padding:'2px 6px' }} onClick={() => setLines(ls => ls.filter(x => x.key !== l.key))}>✕</button> : <span />}
            {isInput && l.item_id && l.loc && Number(l.qty) > have && <div style={{ gridColumn:'1 / -1', fontSize:11, color:'#c53030' }}>Only {have} of {it?.sku} at {l.loc}</div>}
            {!isInput && l.loc && !LOCATIONS[l.loc] && <div style={{ gridColumn:'1 / -1', fontSize:11, color:'#c53030' }}>{l.loc} isn’t a known location</div>}
          </div>
        );
      })}
      {!single && <button className="btn" style={{ fontSize:11 }} onClick={() => setLines(ls => [...ls, blankLine()])}>+ Add {isInput ? 'input' : 'output'}</button>}
    </div>
  );
}

function BatchRecorders({ boot, onToast, onSaved }) {
  const [ids, setIds] = useState(boot.recorders.map(r => r.id));
  const save = async () => {
    try { await batchCall('ims_batch_settings_save', { recorder_ids: ids }); onToast('Batch recorders saved', 'success'); onSaved(); }
    catch (e) { onToast(e.message, 'error'); }
  };
  return (
    <div className="card" style={{ padding:16, marginBottom:16 }}>
      <div style={{ fontWeight:700, fontSize:13, marginBottom:8 }}>Who can record batches (admins only)</div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
        {boot.staff.map(s => (
          <label key={s.id} style={{ fontSize:12, display:'flex', gap:4, alignItems:'center', padding:'3px 8px', border:'1px solid #e2e6ea', borderRadius:6 }}>
            <input type="checkbox" checked={ids.includes(s.id)} onChange={e => setIds(x => e.target.checked ? [...x, s.id] : x.filter(y => y !== s.id))} />{s.name}
          </label>
        ))}
      </div>
      <button className="btn btn-primary" style={{ fontSize:12, marginTop:10 }} disabled={!ids.length} onClick={save}>Save</button>
    </div>
  );
}

function ProductionModule({ items, inventory, onSave, onToast }) {
  const [boot, setBoot] = useState(null);
  const [type, setType] = useState('standard');
  const [inputs, setInputs] = useState([blankLine()]);
  const [outputs, setOutputs] = useState([blankLine()]);
  const [loose, setLoose] = useState('');
  const [boxSize, setBoxSize] = useState('CP11');
  const [boxQty, setBoxQty] = useState('96');
  const [outerLoc, setOuterLoc] = useState('');
  const [coolerLoc, setCoolerLoc] = useState('');
  const [unitLoc, setUnitLoc] = useState('R03');
  const [lot, setLot] = useState('');
  const [notes, setNotes] = useState('');
  const [review, setReview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showRecorders, setShowRecorders] = useState(false);

  const load = useCallback(async () => {
    try { setBoot(await batchCall('ims_batch_bootstrap')); } catch (e) { onToast('Couldn’t load production: ' + e.message, 'error'); }
  }, [onToast]);
  useEffect(() => { load(); }, [load]);

  const bySku = Object.fromEntries(items.map(i => [i.sku, i]));
  const byId = Object.fromEntries(items.map(i => [i.id, i]));
  const firstInput = inputs[0]?.item_id;
  // Carry the lot from the most recent receipt of the main input.
  useEffect(() => {
    if (!firstInput || lot) return;
    let live = true;
    imsQuery('ims_receipts_list', { item_id: firstInput }).then(r => { const l = (r || []).find(x => x.lot_number); if (live && l) setLot(l.lot_number); }).catch(() => {});
    return () => { live = false; };
  }, [firstInput]); // eslint-disable-line react-hooks/exhaustive-deps
  // Box assembly: default the input locations to where the parts are.
  const box = boot?.boxes?.[boxSize];
  useEffect(() => {
    if (!box) return;
    const best = (sku) => { const it = bySku[sku]; const s = it ? inventory.filter(r => r.item_id === it.id && r.qty > 0).sort((a, b) => b.qty - a.qty) : []; return s[0]?.loc || ''; };
    setOuterLoc(best(box.outer)); setCoolerLoc(best(box.cooler)); setBoxQty(String(box.skid));
  }, [boxSize, boot]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!boot) return <div style={{ padding:48, textAlign:'center', color:'#a0aec0' }}>Loading production…</div>;
  const canRecord = boot.me.canRecord;
  const reset = () => { setInputs([blankLine()]); setOutputs([blankLine()]); setLoose(''); setLot(''); setNotes(''); setReview(false); };

  // Live expected vs actual (the server recomputes the same thing when posting).
  const n = (v) => Number(v || 0);
  const ins = inputs.filter(l => l.item_id && n(l.qty) > 0), outs = outputs.filter(l => l.item_id && n(l.qty) > 0);
  const inQty = ins.reduce((a, l) => a + n(l.qty), 0), outQty = outs.reduce((a, l) => a + n(l.qty), 0);
  const cakeOut = type === 'standard' && outs.length > 0 && outs.every(l => l.item_id === outs[0].item_id) ? boot.crabCakes[byId[outs[0].item_id]?.sku] : null;
  const cakeItemId = cakeOut ? outs[0].item_id : null;
  const carryIn = cakeItemId ? Number((boot.carry.find(c => c.item_id === cakeItemId) || {}).carry_over_qty || 0) : 0;
  let expected = null, actual = null, unit = 'lbs';
  if (type === 'box_assembly') { expected = actual = n(boxQty); unit = 'units'; }
  else if (type === 'fish') { expected = Math.floor(inQty / 1.5); actual = outQty; unit = '4-packs'; }
  else if (cakeOut) { expected = Math.round((inQty / 36) * cakeOut.per36 + carryIn); actual = outQty * cakeOut.perUnit + n(loose); unit = 'cakes'; }
  else { expected = inQty; actual = outQty; }
  const diff = actual - expected;
  const pct = expected ? (actual / expected) * 100 : null;
  const flagged = type !== 'box_assembly' && (expected ? Math.abs(diff) / expected > 0.05 : actual > 0);
  const value = (ls) => ls.reduce((a, l) => a + n(l.qty) * Number(byId[l.item_id]?.unit_cost || 0), 0);

  const problems = [];
  if (type === 'box_assembly') {
    if (!(n(boxQty) > 0)) problems.push('Enter how many');
    if (!outerLoc || !coolerLoc) problems.push('Pick where the outer boxes and coolers come from');
    if (!LOCATIONS[normLoc(unitLoc)]) problems.push('Pick where the completed units go');
    const haveAt = (sku, loc) => inventory.find(r => r.item_id === bySku[sku]?.id && r.loc === loc)?.qty ?? 0;
    if (box && outerLoc && n(boxQty) > haveAt(box.outer, outerLoc)) problems.push(`Only ${haveAt(box.outer, outerLoc)} ${box.outer} at ${outerLoc}`);
    if (box && coolerLoc && n(boxQty) > haveAt(box.cooler, coolerLoc)) problems.push(`Only ${haveAt(box.cooler, coolerLoc)} ${box.cooler} at ${coolerLoc}`);
  } else {
    if (!ins.length) problems.push('Add an input');
    if (!outs.length) problems.push('Add an output');
    if ([...ins, ...outs].some(l => !l.loc)) problems.push('Every line needs a location');
    if (outs.some(l => !LOCATIONS[l.loc])) problems.push('Check the output locations');
    if (ins.some(l => n(l.qty) > (inventory.find(r => r.item_id === l.item_id && r.loc === l.loc)?.qty ?? 0))) problems.push('An input is more than what’s there');
    if (type === 'grading' && new Set(ins.map(l => l.item_id)).size !== 1) problems.push('Grading takes one input item');
    if (type === 'fish' && (new Set(ins.map(l => l.item_id)).size !== 1 || new Set(outs.map(l => l.item_id)).size !== 1)) problems.push('Fish is one fish in, one 4-pack item out');
    if (cakeOut && (loose === '' || n(loose) > cakeOut.looseMax)) problems.push(`Enter loose cakes left (0 to ${cakeOut.looseMax})`);
  }
  const ready = canRecord && problems.length === 0;

  const post = async () => {
    setSaving(true);
    try {
      const payload = type === 'box_assembly'
        ? { batch_type: type, box_size: boxSize, qty: n(boxQty), outer_loc: outerLoc, cooler_loc: coolerLoc, unit_loc: normLoc(unitLoc), lot_number: lot, notes }
        : { batch_type: type, inputs: ins.map(l => ({ item_id: l.item_id, loc: l.loc, qty: n(l.qty), lot })), outputs: outs.map(l => ({ item_id: l.item_id, loc: l.loc, qty: n(l.qty) })),
            loose_cakes: cakeOut ? n(loose) : undefined, lot_number: lot, notes };
      const r = await batchCall('ims_batch_post', payload);
      onToast(`${r.batch_number} posted${r.flagged ? ` · ⚠ yield ${r.pct}% of expected, flagged for review` : ''}`, r.flagged ? 'error' : 'success');
      reset(); await load(); onSave();
    } catch (e) { onToast('Post failed: ' + e.message, 'error'); }
    finally { setSaving(false); }
  };

  const yieldBox = (
    <div className="card" style={{ padding:16, marginTop:16, borderLeft:`4px solid ${flagged ? '#f6ad55' : '#68d391'}` }}>
      <div style={{ display:'flex', gap:24, flexWrap:'wrap', fontSize:13 }}>
        <div>Expected <strong>{expected ?? '—'}</strong> {unit}</div>
        <div>Actual <strong>{actual ?? '—'}</strong> {unit}</div>
        {type !== 'box_assembly' && <div style={{ color: diff < 0 ? '#c53030' : diff > 0 ? '#276749' : '#4a5568' }}>{diff > 0 ? 'Yield gain' : diff < 0 ? 'Yield loss' : 'On target'} <strong>{diff > 0 ? '+' : ''}{diff}</strong>{pct != null ? ` (${pct.toFixed(1)}%)` : ''}</div>}
        {flagged && <div style={{ color:'#92600a' }}>⚠ More than 5% off. It still posts, and gets flagged for review.</div>}
      </div>
      {cakeOut && <div style={{ fontSize:12, color:'#718096', marginTop:6 }}>Includes {carryIn} loose {cakeOut.size} cake{carryIn === 1 ? '' : 's'} carried from the last batch. 36 lb makes {cakeOut.per36} cakes ({cakeOut.per36 / cakeOut.perUnit} units).</div>}
      {type === 'grading' && <div style={{ fontSize:12, color:'#718096', marginTop:6 }}>Value in {fmtMoney(value(ins))} → value out {fmtMoney(value(outs))} at item master costs.</div>}
    </div>
  );

  return (
    <div>
      {!canRecord && (
        <div className="card" style={{ padding:'10px 16px', marginBottom:16, fontSize:12, color:'#718096' }}>
          Only {boot.recorders.map(r => r.name).join(', ') || 'the batch recorders'} can record batches. You can see the history below.
        </div>
      )}
      {boot.me.isAdmin && (
        <div style={{ marginBottom:12 }}>
          <button className="btn" style={{ fontSize:11 }} onClick={() => setShowRecorders(v => !v)}>⚙ Who can record{boot.recorderSource === 'name-match' ? ' (matched by name)' : ''}</button>
        </div>
      )}
      {showRecorders && boot.me.isAdmin && <BatchRecorders boot={boot} onToast={onToast} onSaved={() => { setShowRecorders(false); load(); }} />}

      {canRecord && !review && (
        <div className="card" style={{ padding:20, marginBottom:20 }}>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:8 }}>
            {BATCH_TYPES.map(t => <button key={t.key} className={`btn ${type === t.key ? 'btn-primary' : ''}`} style={{ fontSize:12 }} onClick={() => { setType(t.key); reset(); }}>{t.label}</button>)}
          </div>
          <div style={{ fontSize:12, color:'#718096', marginBottom:16 }}>{BATCH_TYPES.find(t => t.key === type).help}</div>

          {type === 'box_assembly' ? (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4, minmax(140px,1fr))', gap:12, alignItems:'end' }}>
              <div>
                <label style={labelStyle}>Box</label>
                <select className="form-input" value={boxSize} onChange={e => setBoxSize(e.target.value)}>
                  {Object.entries(boot.boxes).map(([k, b]) => <option key={k} value={k}>{k}: {b.outer} + {b.cooler} → {b.unit} ({b.skid}/skid)</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>How many (full skid = {box?.skid})</label><QtyInput value={boxQty} onChange={setBoxQty} /></div>
              <div>
                <label style={labelStyle}>{box?.outer} outer boxes from</label>
                <select className="form-input" value={outerLoc} onChange={e => setOuterLoc(e.target.value)}>
                  <option value="">—</option>
                  {inventory.filter(r => r.item_id === bySku[box?.outer]?.id && r.qty > 0).map(r => <option key={r.loc} value={r.loc}>{r.loc} ({r.qty})</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>{box?.cooler} coolers from</label>
                <select className="form-input" value={coolerLoc} onChange={e => setCoolerLoc(e.target.value)}>
                  <option value="">—</option>
                  {inventory.filter(r => r.item_id === bySku[box?.cooler]?.id && r.qty > 0).map(r => <option key={r.loc} value={r.loc}>{r.loc} ({r.qty})</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>{box?.unit} completed units to</label><input className="form-input" value={unitLoc} onChange={e => setUnitLoc(normLoc(e.target.value))} style={{ fontFamily:'monospace' }} /></div>
            </div>
          ) : (
            <div style={{ display:'flex', gap:24, flexWrap:'wrap' }}>
              <BatchLines title={type === 'fish' ? 'Fish in (lbs)' : 'Inputs (come out)'} lines={inputs} setLines={setInputs} items={items} inventory={inventory} isInput single={type === 'grading' || type === 'fish'} />
              <BatchLines title={type === 'fish' ? '4-packs cut' : type === 'grading' ? 'Graded sizes (go in)' : 'Outputs (go in)'} lines={outputs} setLines={setOutputs} items={items} inventory={inventory} single={type === 'fish'} />
            </div>
          )}

          {cakeOut && (
            <div style={{ marginTop:16, padding:12, borderRadius:8, background:'#f8f9fb', border:'1px solid #e2e6ea', display:'flex', gap:16, alignItems:'center', flexWrap:'wrap' }}>
              <div style={{ fontSize:13 }}>Loose {cakeOut.size} cakes carried in: <strong>{carryIn}</strong></div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <label style={{ ...labelStyle, marginBottom:0 }}>Loose cakes left over (0–{cakeOut.looseMax})</label>
                <QtyInput value={loose} onChange={v => setLoose(v === '' ? '' : String(Math.min(Number(v), 99)))} style={{ width:70 }} />
              </div>
              <div style={{ fontSize:11, color:'#a0aec0' }}>Loose cakes stay out of inventory and get added to the next batch.</div>
            </div>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'200px 1fr', gap:12, marginTop:16 }}>
            <div><label style={labelStyle}>Lot number (optional)</label><input className="form-input" value={lot} onChange={e => setLot(e.target.value)} placeholder="Carried to the outputs" /></div>
            <div><label style={labelStyle}>Notes (optional)</label><input className="form-input" value={notes} onChange={e => setNotes(e.target.value)} /></div>
          </div>

          {yieldBox}
          {problems.length > 0 && <div style={{ fontSize:11, color:'#a0aec0', marginTop:8 }}>{problems.join(' · ')}</div>}
          <button className="btn btn-primary" style={{ marginTop:12 }} disabled={!ready} onClick={() => setReview(true)}>Review batch</button>
        </div>
      )}

      {canRecord && review && (
        <div className="card" style={{ padding:20, marginBottom:20, maxWidth:720 }}>
          <h3 style={{ fontSize:15, fontWeight:700, marginBottom:12 }}>Confirm {BATCH_TYPES.find(t => t.key === type).label.toLowerCase()} batch</h3>
          {type === 'box_assembly' ? (
            <>
              <SumRow label="Out">{boxQty} {box.outer} from {outerLoc}, {boxQty} {box.cooler} from {coolerLoc}</SumRow>
              <SumRow label="In">{boxQty} {box.unit} into {normLoc(unitLoc)}</SumRow>
            </>
          ) : (
            <>
              <SumRow label="Out">{ins.map(l => `${l.qty} ${byId[l.item_id]?.sku} from ${l.loc}`).join(', ')}</SumRow>
              <SumRow label="In">{outs.map(l => `${l.qty} ${byId[l.item_id]?.sku} into ${l.loc}`).join(', ')}</SumRow>
            </>
          )}
          {cakeOut && <SumRow label="Loose cakes">{carryIn} carried in → {loose} carried over</SumRow>}
          <SumRow label="Yield" warn={flagged}>{expected} expected, {actual} actual {unit}{type !== 'box_assembly' ? ` (${diff > 0 ? '+' : ''}${diff})` : ''}{flagged ? ' · flagged, over 5%' : ''}</SumRow>
          {lot && <SumRow label="Lot">{lot}</SumRow>}
          <div style={{ fontSize:12, color:'#718096', marginTop:10 }}>This posts right away.</div>
          <div style={{ display:'flex', gap:8, marginTop:14 }}>
            <button className="btn btn-primary" onClick={post} disabled={saving}>{saving ? 'Posting…' : 'Post batch'}</button>
            <button className="btn" onClick={() => setReview(false)} disabled={saving}>Back</button>
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ padding:'12px 16px', borderBottom:'1px solid #e2e6ea', fontWeight:700, fontSize:13 }}>Recent batches</div>
        {boot.batches.length === 0 ? <EmptyState icon="🏭" title="No batches yet" /> : (
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>Batch</th><th>Type</th><th>In</th><th>Out</th><th style={{textAlign:'right'}}>Expected</th><th style={{textAlign:'right'}}>Actual</th><th>Yield</th><th>Lot</th><th>By</th></tr></thead>
              <tbody>
                {boot.batches.map(b => {
                  const fmt = (t) => b.lines.filter(l => l.line_type === t).map(l => `${l.qty} ${byId[l.item_id]?.sku || '?'} ${t === 'input' ? '@' : '→'} ${l.loc}`).join(', ');
                  const d = Number(b.yield_diff || 0);
                  return (
                    <tr key={b.id} style={b.yield_flag ? { background:'rgba(214,158,46,0.08)' } : undefined}>
                      <td style={{ fontFamily:'monospace', fontSize:11, whiteSpace:'nowrap' }}>{b.batch_number}<div style={{ color:'#a0aec0' }}>{fmtDate(b.created_at)}</div></td>
                      <td style={{ fontSize:12 }}>{(BATCH_TYPES.find(t => t.key === b.batch_type) || {}).label || b.batch_type}</td>
                      <td style={{ fontSize:11 }}>{fmt('input')}</td>
                      <td style={{ fontSize:11 }}>{fmt('output')}{b.carry_over_notes && <div style={{ color:'#a0aec0' }}>{b.carry_over_notes}</div>}</td>
                      <td style={{ textAlign:'right' }}>{b.expected_qty != null ? Number(b.expected_qty) : '—'}</td>
                      <td style={{ textAlign:'right' }}>{b.actual_qty != null ? Number(b.actual_qty) : '—'}</td>
                      <td style={{ fontSize:12 }}>
                        {b.batch_type === 'box_assembly' ? '1 to 1' : <span style={{ color: d < 0 ? '#c53030' : d > 0 ? '#276749' : '#4a5568' }}>{d > 0 ? '+' : ''}{d} {b.yield_unit}</span>}
                        {b.yield_flag && <div><Badge color="yellow">⚠ Over 5%</Badge></div>}
                      </td>
                      <td style={{ fontSize:11 }}>{b.lot_number || '—'}</td>
                      <td style={{ fontSize:11 }}>{b.recorded_by}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────────────────────

export default function Inventory({ navigateTo }) {
  const [module, setModule] = useState('inventory');
  const [sub, setSub] = useState('view');

  const [items, setItems]               = useState([]);
  const [inventory, setInventoryData]   = useState([]);
  const [toReview, setToReview]         = useState(0);
  const [ownerSettings, setOwnerSettings] = useState({ gross_profit:85000, prev_total_value:0, month:'' });
  const [snapshots, setSnapshots]       = useState([]);
  const [par, setPar]                   = useState({});
  const [vendors, setVendors]           = useState([]);
  const [holds, setHolds]               = useState([]);
  const isAdmin = isAdminUser();
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [toast, setToast]               = useState({ msg:'', type:'' });
  const toastTimer = useRef(null);

  // Stable identity: count screens reload when this changes.
  const showToast = useCallback((msg, type='info') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, type });
    toastTimer.current = setTimeout(() => setToast({ msg:'', type:'' }), 3500);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [it, inv, os, snaps, vend, hl, cc, pr] = await Promise.all([
        loadItems(), loadInventory(), loadOwnerSettings(), loadSnapshots(),
        loadVendors(), loadOpenHolds(), ccCall('ims_cc_bootstrap').catch(() => null),
        loadPar().catch(() => null),
      ]);
      if (pr) setPar(pr);
      setItems(it); setInventoryData(inv); setOwnerSettings(os); setSnapshots(snaps);
      setVendors(vend); setHolds(hl);
      setToReview(cc && cc.me && cc.me.isReviewer ? cc.sessions.filter(x => x.status === 'submitted' && !x.i_counted).length : 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);


  const navItems = [
    { key:'inventory', label:'Manage Inventory', icon:'📦' },
    { key:'items',     label:'Items',             icon:'🗂' },
    { key:'cyclecount',label:'Cycle Count',        icon:'📋' },
    { key:'review',    label:`Review & Approve${toReview>0?` (${toReview})`:''}`, icon:'✅' },
    { key:'owner',     label:'Owner Summary',      icon:'📊' },
    { key:'auditlog',  label:'Audit Log',           icon:'📜' },
    { key:'deduct',    label:'Daily Deduct',        icon:'📉' },
    { key:'production',label:'Production',          icon:'🏭' },
  ];

  return (
    <div>
      <div className="breadcrumb">
        <a onClick={() => navigateTo('dashboard')} style={{ cursor:'pointer' }}>Dashboard</a>
        <span>›</span><span>CPx Catch</span>
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:800, color:'#1a202c', margin:0 }}>CPx Catch</h2>
          <div style={{ fontSize:12, color:'#a0aec0', marginTop:2 }}>
            {items.length} SKUs · {inventory.reduce((s,r)=>s+(r.qty??0),0).toLocaleString()} total units
          </div>
        </div>
        <button onClick={loadAll} disabled={loading} className="btn btn-primary" style={{ fontSize:12 }}>
          {loading ? '⟳ Loading…' : '↻ Refresh'}
        </button>
      </div>

      <div style={{ display:'flex', gap:4, marginBottom:20, borderBottom:'2px solid #e2e6ea', paddingBottom:0 }}>
        {navItems.map(n => (
          <button key={n.key} onClick={() => { setModule(n.key); if(n.key==='inventory') setSub('view'); }}
            style={{ background:'none', border:'none', cursor:'pointer', padding:'10px 16px', fontSize:13, fontWeight:module===n.key?700:500, color:module===n.key?'#e8550a':'#4a5568', borderBottom:module===n.key?'2px solid #e8550a':'2px solid transparent', marginBottom:-2, whiteSpace:'nowrap', transition:'all 0.15s' }}>
            {n.icon} {n.label}
          </button>
        ))}
      </div>

      {module === 'inventory' && (
        <div style={{ display:'flex', gap:8, marginBottom:16 }}>
          {[{key:'view',label:'👁 View'},{key:'put',label:'📥 Put / Receive'},{key:'move',label:'↔ Move'},{key:'damage',label:`⚠ Damage / Waste${holds.length ? ` (${holds.length})` : ''}`}].map(s => (
            <button key={s.key} onClick={()=>setSub(s.key)} className={`btn ${sub===s.key?'btn-primary':''}`} style={{ fontSize:12 }}>{s.label}</button>
          ))}
        </div>
      )}

      {error && (
        <div className="card" style={{ padding:16, color:'#c53030', fontSize:12, marginBottom:16, borderLeft:'4px solid #fc8181' }}>
          <strong>Error:</strong> {error}
          <button className="btn" onClick={loadAll} style={{ marginLeft:12, fontSize:11 }}>Retry</button>
        </div>
      )}

      {loading && items.length === 0 ? (
        <div style={{ padding:48, textAlign:'center', color:'#a0aec0' }}>
          <div style={{ fontSize:24, marginBottom:8 }}>⟳</div>Loading inventory data…
        </div>
      ) : (
        <>
          {module==='inventory'&&sub==='view'  && <InventoryView items={items} inventory={inventory} holds={holds} par={par} onToast={showToast} />}
          {module==='inventory'&&sub==='put'   && <PutInventory items={items} inventory={inventory} vendors={vendors} onSave={loadAll} onToast={showToast} />}
          {module==='inventory'&&sub==='damage'&& <DamageModule items={items} inventory={inventory} vendors={vendors} holds={holds} onSave={loadAll} onToast={showToast} />}
          {module==='inventory'&&sub==='move'  && <MoveInventory items={items} inventory={inventory} onSave={loadAll} onToast={showToast} />}
          {module==='items'                    && <ItemsModule items={items} par={par} isAdmin={isAdmin} onSave={loadAll} onToast={showToast} />}
          {module==='cyclecount'               && <CountModule items={items} inventory={inventory} onSave={loadAll} onToast={showToast} />}
          {module==='review'                   && <ReviewModule onSave={loadAll} onToast={showToast} />}
          {module==='owner'                    && <OwnerSummary items={items} inventory={inventory} ownerSettings={ownerSettings} snapshots={snapshots} isAdmin={isAdmin} onSave={loadAll} onToast={showToast} />}
          {module==='auditlog'                 && <AuditLog items={items} />}
          {module==='deduct'                    && <DailyDeduct items={items} inventory={inventory} onSave={loadAll} onToast={showToast} />}
          {module==='production'                && <ProductionModule items={items} inventory={inventory} onSave={loadAll} onToast={showToast} />}
        </>
      )}

      <Toast msg={toast.msg} type={toast.type} />
    </div>
  );
}
